import React, { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Wallet, Sparkles, X, Copy, CheckCircle2, XCircle, Check, ChevronDown,
  ChevronLeft, ChevronRight, RefreshCw, UserRound, AlertTriangle, ArrowLeftRight, Eye, EyeOff,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';
import SafeImage from '../../../components/SafeImage';
import WalletTransferPanel from './WalletTransferPanel';

/* -------------------- FULL-SCREEN WHITE PANEL -------------------- */
export function FullScreenWhitePanel({ children, onClose, testid }) {
  return (
    <div
      className="fixed inset-0 z-[50] flex flex-col"
      style={{ background: '#004D61', color: '#FFFFFF', fontFamily: "'Plus Jakarta Sans', sans-serif", paddingBottom: '96px' }}
      data-testid={testid}
    >
      {children}
    </div>
  );
}

/* -------------------- STEPPER (dynamic 2 or 3 steps) -------------------- */
export function Stepper({ step, accent, accentDark, labels = ['Payment method', 'Pay'] }) {
  const Step = ({ n, label, state }) => {
    const isActive = state === 'active';
    const isDone = state === 'done';
    const ringColor = isActive || isDone ? accent : '#D1D5DB';
    const fillColor = isDone ? accent : 'transparent';
    const numColor = isDone ? '#fff' : (isActive ? accentDark : '#9CA3AF');
    const labelColor = isActive || isDone ? accentDark : '#9CA3AF';
    return (
      <div className="flex flex-col items-center gap-2 shrink-0">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center display font-extrabold text-base transition-all"
          style={{
            border: `3px solid ${ringColor}`,
            background: fillColor,
            color: numColor,
            boxShadow: isActive ? `0 0 0 6px ${accent}1F` : 'none'
          }}
          data-testid={`stepper-${n}`}
        >
          {isDone ? <Check className="w-5 h-5" strokeWidth={4} /> : n}
        </div>
        <span className="text-xs sm:text-sm display font-bold tracking-wide text-center" style={{ color: labelColor }}>
          {label}
        </span>
      </div>
    );
  };
  return (
    <div className="flex items-start justify-center gap-2 sm:gap-3 mb-2" data-testid="wallet-stepper">
      {labels.map((label, idx) => {
        const n = idx + 1;
        const state = step > n ? 'done' : step === n ? 'active' : 'idle';
        return (
          <React.Fragment key={n}>
            <Step n={n} label={label} state={state} />
            {idx < labels.length - 1 && (
              <div
                className="flex-1 max-w-[140px] h-[3px] rounded-full mt-[22px] transition-colors"
                style={{ background: step > n ? accent : '#E5E7EB' }}
              />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

/* -------------------- ROW (small label/value pair) -------------------- */
export function Row({ label, value, valueBold, mono }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="uppercase tracking-wider text-xs font-bold" style={{ color: '#6B7280' }}>{label}</span>
      <span
        className={`text-right ${mono ? 'mono' : ''} ${valueBold ? 'display font-extrabold' : 'font-medium'}`}
        style={{ color: '#0B0F0C', wordBreak: 'break-word', maxWidth: '60%' }}
      >
        {value}
      </span>
    </div>
  );
}

/* -------------------- PAYMENT METHOD LOGO --------------------
   Renders an authentic-looking brand mark for the supported gateway
   methods (cashapp / applepay / googlepay), and falls back to a
   generic Wallet icon for tenant-custom manual methods.
*/
export function PaymentMethodLogo({ method, accent, accentDark, size = 'md' }) {
  const code = (method?.method || method?.gateway_method || '').toLowerCase();
  const dim = size === 'sm' ? 44 : 48;
  const iconPx = size === 'sm' ? 22 : 26;
  const rawLogoUrl = String(method?.logo_url || '').trim();
  const resolvedLogoUrl = rawLogoUrl
    ? (rawLogoUrl.startsWith('http://') || rawLogoUrl.startsWith('https://')
        ? rawLogoUrl
        : `${process.env.REACT_APP_BACKEND_URL || ''}${rawLogoUrl}`)
    : '';

  if (resolvedLogoUrl) {
    return (
      <span
        className="rounded-xl flex items-center justify-center shrink-0 overflow-hidden"
        style={{
          width: dim,
          height: dim,
          background: '#FFFFFF',
          border: '1px solid rgba(0,0,0,0.10)',
          boxShadow: '0 4px 10px rgba(0,0,0,0.10)',
        }}
        aria-hidden
      >
        <SafeImage
          src={resolvedLogoUrl}
          alt={method?.title || method?.method || 'Payment method'}
          className="w-full h-full object-contain"
          loading="lazy"
          fallback={<Wallet className="w-6 h-6" style={{ color: accentDark || accent || '#15803D' }} />}
        />
      </span>
    );
  }

  if (code === 'cashapp' || code.includes('cash')) {
    return (
      <span
        className="rounded-xl flex items-center justify-center shrink-0"
        style={{ width: dim, height: dim, background: '#00D632', boxShadow: '0 4px 10px rgba(0,214,50,0.4)' }}
        aria-hidden
      >
        <svg viewBox="0 0 24 24" width={iconPx} height={iconPx}>
          <path fill="#FFFFFF" d="M19.6 4.4C18.5 3.3 17.1 3 15.7 3H8.3c-1.4 0-2.8.3-3.9 1.4C3.3 5.5 3 6.9 3 8.3v7.4c0 1.4.3 2.8 1.4 3.9 1.1 1.1 2.5 1.4 3.9 1.4h7.4c1.4 0 2.8-.3 3.9-1.4 1.1-1.1 1.4-2.5 1.4-3.9V8.3c0-1.4-.3-2.8-1.4-3.9zM14.2 9.6c-.3-.3-.7-.6-1.4-.7l-.1-1c0-.2-.1-.3-.3-.3h-.7c-.2 0-.3.1-.3.3l-.1.9c-1.4.1-2.5.9-2.5 2.3 0 1.2.8 1.8 2 2.2.9.3 1.4.5 1.4 1 0 .4-.4.7-1.1.7-.7 0-1.4-.3-1.9-.7-.1-.1-.3-.1-.4 0l-.7.7c-.1.1-.1.3 0 .4.5.4 1.2.8 2 1l-.1 1c0 .2.1.3.3.3h.7c.2 0 .3-.1.3-.3l.1-1c1.4-.2 2.4-1 2.4-2.4 0-1.2-.8-1.8-2.2-2.3-.9-.3-1.3-.5-1.3-.9s.4-.6 1-.6c.6 0 1.2.2 1.7.6.1.1.3.1.4 0l.7-.7c.1-.1.1-.3 0-.4z"/>
        </svg>
      </span>
    );
  }

  if (code === 'applepay' || code === 'apple_pay' || code.includes('apple')) {
    return (
      <span
        className="rounded-xl flex items-center justify-center shrink-0"
        style={{ width: dim, height: dim, background: '#000000', boxShadow: '0 4px 10px rgba(0,0,0,0.4)' }}
        aria-hidden
      >
        <svg viewBox="0 0 60 24" width={iconPx + 14} height={iconPx}>
          <path fill="#FFFFFF" d="M11.55 4.36a3.18 3.18 0 0 0-.74 2.39 3.07 3.07 0 0 0 2.34-1.21 3.36 3.36 0 0 0 .79-2.41 3.4 3.4 0 0 0-2.39 1.23zm2.34 1.85c-1.29-.07-2.39.73-3 .73s-1.56-.7-2.58-.68a3.84 3.84 0 0 0-3.27 2C3.69 11 5 16.05 6.46 18.81c.74 1.36 1.61 2.86 2.76 2.82s1.51-.71 2.84-.71 1.7.71 2.84.69 1.93-1.36 2.65-2.72a11.94 11.94 0 0 0 1.21-2.51 3.92 3.92 0 0 1-2.36-3.57 4 4 0 0 1 1.91-3.34 4.18 4.18 0 0 0-3.27-1.76M27.7 4.7v15.7h2.4v-5.32h3.32c3.05 0 5.18-2.06 5.18-5.2 0-3.13-2.1-5.18-5.1-5.18zM30.1 6.8h2.74c2.06 0 3.24 1.1 3.24 3.06s-1.18 3.08-3.25 3.08H30.1zm12.81 13.83c1.7 0 3.16-.84 3.85-2.18h.05v2.05h2.21v-7.7c0-2.21-1.79-3.65-4.55-3.65-2.56 0-4.46 1.46-4.5 3.46h2.16c.18-1 1.07-1.62 2.27-1.62 1.46 0 2.31.71 2.31 2v.92l-3.07.18c-2.7.16-4.16 1.36-4.16 3.4 0 2.06 1.55 3.14 3.43 3.14zm.65-1.84c-1.27 0-2.07-.61-2.07-1.55 0-.97.78-1.53 2.27-1.62l2.74-.18v.95c0 1.45-1.23 2.4-2.94 2.4zM52.27 24c2.32 0 3.41-.92 4.36-3.69l4.18-12.34h-2.43L55.55 17.6h-.05L52.7 7.97h-2.51l4.05 11.71-.22.71c-.36 1.18-.96 1.65-2.06 1.65-.2 0-.59-.02-.74-.04v1.86c.15.04.78.05.91.05z"/>
        </svg>
      </span>
    );
  }

  if (code === 'googlepay' || code === 'google_pay' || code.includes('google')) {
    return (
      <span
        className="rounded-xl flex items-center justify-center shrink-0"
        style={{ width: dim, height: dim, background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.12)', boxShadow: '0 4px 10px rgba(0,0,0,0.08)' }}
        aria-hidden
      >
        <svg viewBox="0 0 56 24" width={iconPx + 14} height={iconPx}>
          <path fill="#5F6368" d="M26.54 11.66v4.93h-1.56V4.51h4.18a3.78 3.78 0 0 1 2.71 1.06 3.45 3.45 0 0 1 0 5.07 3.81 3.81 0 0 1-2.71 1.02zm0-5.66v4.16h2.66a2.4 2.4 0 0 0 1.79-.71 2.34 2.34 0 0 0 0-3.32 2.4 2.4 0 0 0-1.79-.7zM37 8.14a4.7 4.7 0 0 1 3.32 1.16 3.96 3.96 0 0 1 1.21 3.05v6.16h-1.49v-1.16h-.07c-.65 1-1.78 1.6-2.97 1.56a3.85 3.85 0 0 1-2.66-.94 3.04 3.04 0 0 1-1.05-2.34c0-.99.37-1.78 1.12-2.36a4.66 4.66 0 0 1 2.97-.86 5.15 5.15 0 0 1 2.62.58v-.36a2.05 2.05 0 0 0-.71-1.56 2.45 2.45 0 0 0-1.66-.66 2.6 2.6 0 0 0-2.27 1.21l-1.36-.86A4.04 4.04 0 0 1 37 8.14m-2 6.05c0 .43.21.84.55 1.09.39.31.87.46 1.36.45a2.79 2.79 0 0 0 1.97-.81 2.61 2.61 0 0 0 .87-1.92 3.65 3.65 0 0 0-2.32-.66c-.71 0-1.31.18-1.79.51-.43.32-.65.74-.65 1.34M52 8.41 46.79 20.36h-1.61l1.95-4.2-3.43-7.75h1.7l2.49 6h.04l2.42-6z"/>
          <path fill="#4285F4" d="M19.36 12.65a8.37 8.37 0 0 0-.13-1.5h-7v2.83h4a3.42 3.42 0 0 1-1.49 2.25v1.85h2.4c1.4-1.3 2.22-3.21 2.22-5.43z"/>
          <path fill="#34A853" d="M12.23 19.92c2 0 3.69-.66 4.91-1.79l-2.4-1.85c-.67.45-1.52.71-2.51.71-1.92 0-3.55-1.3-4.13-3.04H5.61v1.91a7.4 7.4 0 0 0 6.62 4.06z"/>
          <path fill="#FBBC04" d="M8.1 13.95a4.43 4.43 0 0 1 0-2.83V9.21H5.61a7.4 7.4 0 0 0 0 6.65z"/>
          <path fill="#EA4335" d="M12.23 8.07a4.01 4.01 0 0 1 2.83 1.11l2.12-2.13a7.13 7.13 0 0 0-4.95-1.93 7.4 7.4 0 0 0-6.62 4.07L8.1 11.1c.58-1.74 2.21-3.04 4.13-3.04z"/>
        </svg>
      </span>
    );
  }

  return (
    <span
      className="rounded-xl flex items-center justify-center shrink-0"
      style={{
        width: dim, height: dim,
        background: `${accent}1A`,
        border: `1px solid ${accent}55`,
      }}
      aria-hidden
    >
      <Wallet style={{ width: iconPx, height: iconPx, color: accentDark }} />
    </span>
  );
}

const gatewayCode = (method) => String(
  method?.gateway_method_code
  || method?.gateway_method
  || method?.method
  || method?.code
  || ''
).toLowerCase().replace(/[^a-z0-9]+/g, '');

const gatewaySubmethodCode = (method) => String(
  method?.gateway_submethod
  || method?.method
  || method?.gateway_method
  || method?.gateway_method_code
  || method?.code
  || ''
).toLowerCase().replace(/[^a-z0-9]+/g, '');

const isEcashAppCompact = (compact = '') => (
  compact.includes('cashappvialt')
  || compact.includes('cashapplt')
  || compact.includes('cashappvialightning')
  || compact.includes('cashapplightning')
  || compact.includes('ecashapp')
  || compact.includes('ecash')
);

const isCashAppViaLightning = (method) => isEcashAppCompact([
  method?.gateway_submethod,
  method?.gateway_method_code,
  method?.gateway_method,
  method?.method,
  method?.code,
  method?.title,
  method?.name,
  method?.display_name,
  method?.player_badge_label,
  method?.method_details,
].join(' ').toLowerCase().replace(/[^a-z0-9]+/g, ''));

const canonicalApiGatewayCode = (method) => {
  const compact = [
    method?.gateway_submethod,
    method?.gateway_method_code,
    method?.gateway_method,
    method?.method,
    method?.code,
    method?.title,
    method?.name,
    method?.display_name,
    method?.receiving_address,
    method?.method_details,
  ].join(' ').toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (isEcashAppCompact(compact)) return 'cashapp_via_lt';
  if (compact.includes('cashapp')) return 'cashapp';
  if (compact.includes('applepay')) return 'applepay';
  if (compact.includes('googlepay') || compact === 'gpay') return 'googlepay';
  if (compact.includes('paypal')) return 'paypal';
  if (compact.includes('venmo')) return 'venmo';
  if (compact.includes('nowpayments') || compact.includes('crypto') || compact.includes('usdt')) return 'crypto';
  return gatewayCode(method);
};

const canonicalPlayerGatewayKey = (method) => {
  const compact = [
    method?.gateway_submethod,
    method?.gateway_method_code,
    method?.gateway_method,
    method?.method,
    method?.code,
    method?.title,
    method?.name,
    method?.display_name,
    method?.gateway_provider,
    method?.checkout_link_provider_code,
    method?.provider_code,
    method?.base_url,
    method?.api_url,
    method?.checkout_url,
    method?.receiving_address,
    method?.method_details,
  ].join(' ').toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (isEcashAppCompact(compact)) return 'cashapp_via_lt';
  if (compact.includes('cashapp')) return 'cashapp';
  if (compact.includes('applepay')) return 'applepay';
  if (compact.includes('googlepay') || compact === 'gpay') return 'googlepay';
  if (compact.includes('paypal')) return 'paypal';
  if (compact.includes('venmo')) return 'venmo';
  if (
    compact.includes('nowpayments')
    || compact.includes('crypto')
    || compact.includes('usdt')
    || compact.includes('bitcoin')
    || compact.includes('btc')
  ) return 'crypto';
  return canonicalApiGatewayCode(method);
};

const paymentMethodBadgeLabel = (method, mode) => {
  const custom = String(
    method?.player_badge_label
    || method?.player_badge
    || method?.badge_label
    || method?.tag_label
    || method?.display_tag
    || ''
  ).trim();
  if (custom) return custom;
  const bonus = Number(method?.bonus_percent || method?.bonus_or_fee || 0);
  if (mode === 'deposit' && bonus > 0 && (method?.bonus_fee_direction || 'bonus') === 'bonus') {
    return `+${bonus}% BONUS`;
  }
  return '';
};

const depositMethodFinePrint = (method, mode) => {
  if (mode !== 'deposit') return '';
  const code = canonicalApiGatewayCode(method);
  if (code === 'cashapp_via_lt' || isCashAppViaLightning(method)) return 'No fee';
  if (code === 'cashapp') return '2% fee';
  if (method?.min_amount) return `Min $${method.min_amount}`;
  return '';
};

const paymentBadgeStyle = (tone = 'gold', accent = '#F59E0B') => {
  const styles = {
    gold: {
      color: '#7A4A00',
      borderColor: 'rgba(245, 158, 11, 0.48)',
      background: 'linear-gradient(135deg, rgba(255,237,135,0.98), rgba(245,158,11,0.95))',
      boxShadow: '0 0 14px rgba(245, 158, 11, 0.32)',
    },
    green: {
      color: '#065F46',
      borderColor: 'rgba(16,185,129,0.42)',
      background: 'linear-gradient(135deg, rgba(209,250,229,0.98), rgba(52,211,153,0.95))',
      boxShadow: '0 0 14px rgba(16,185,129,0.28)',
    },
    blue: {
      color: '#075985',
      borderColor: 'rgba(56,189,248,0.42)',
      background: 'linear-gradient(135deg, rgba(224,242,254,0.98), rgba(56,189,248,0.92))',
      boxShadow: '0 0 14px rgba(56,189,248,0.28)',
    },
    purple: {
      color: '#4C1D95',
      borderColor: 'rgba(0,107,133,0.42)',
      background: 'linear-gradient(135deg, rgba(243,232,255,0.98), rgba(0,107,133,0.88))',
      boxShadow: '0 0 14px rgba(0,107,133,0.26)',
    },
    red: {
      color: '#881337',
      borderColor: 'rgba(244,63,94,0.42)',
      background: 'linear-gradient(135deg, rgba(255,228,230,0.98), rgba(244,63,94,0.88))',
      boxShadow: '0 0 14px rgba(244,63,94,0.24)',
    },
  };
  return styles[tone] || {
    color: '#111827',
    borderColor: `${accent}55`,
    background: `linear-gradient(135deg, #FFFFFF, ${accent}33)`,
    boxShadow: `0 0 14px ${accent}28`,
  };
};

const cashoutMethodLabels = (method) => {
  const compact = [
    method?.gateway_submethod,
    method?.gateway_method_code,
    method?.gateway_method,
    method?.method,
    method?.code,
    method?.title,
    method?.name,
    method?.display_name,
    method?.receiving_address,
    method?.method_details,
  ].join(' ').toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (compact.includes('cashapp')) {
    return {
      methodName: 'Cash App',
      nameLabel: 'Cash App Name',
      tagLabel: 'Cashtag',
      namePlaceholder: 'Your Cash App name',
      tagPlaceholder: 'Your Cashtag',
    };
  }
  if (compact.includes('chime')) {
    return {
      methodName: 'Chime',
      nameLabel: 'Chime Name',
      tagLabel: 'Chime Tag',
      namePlaceholder: 'Your Chime name',
      tagPlaceholder: 'Your Chime tag',
    };
  }
  const rawTitle = String(method?.title || method?.display_name || method?.name || 'Cashout').trim();
  const methodName = rawTitle || 'Cashout';
  return {
    methodName,
    nameLabel: `${methodName} Name`,
    tagLabel: `${methodName} Tag`,
    namePlaceholder: `Your ${methodName} name`,
    tagPlaceholder: `Your ${methodName} tag`,
  };
};

const isApplePayGateway = (method) => {
  const values = [
    method?.gateway_submethod,
    method?.method,
    method?.gateway_method_code,
    method?.gateway_method,
    method?.code,
    method?.title,
    method?.name,
    method?.display_name,
  ];
  return values.some(value => String(value || '').toLowerCase().replace(/[^a-z0-9]+/g, '').includes('applepay'));
};

const isInternalThreeInOne = (method) => {
  const code = String(method?.method || method?.code || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  return code === '3in1' || code === 'threeinone' || code === 'weblink';
};

const methodMatchesFlow = (method, flowName) => {
  const flow = String(method?.flow || '').toLowerCase();
  return (flow === 'both' || flow === flowName) && !isInternalThreeInOne(method);
};

const API_GATEWAY_CODES = new Set(['cashapp_via_lt', 'cashapp', 'applepay', 'googlepay', 'paypal', 'venmo', 'crypto', 'nowpayments']);
const SLOTS_OCEANS_DEPOSIT_METHODS = new Set(['cashapp_via_lt', 'cashapp', 'applepay', 'chime', 'googlepay', 'crypto']);
const JENNY_PAYMENT_LINK_PROVIDER = 'jenny_payment_link';
const JENNY_PAYMENT_LINK_METHODS = new Set(['cashapp_via_lt', 'cashapp', 'applepay', 'googlepay', 'paypal', 'venmo', 'crypto']);
const WEBSITE_PAYMENT_LINK_PROVIDER = 'website_payment_link';
const WEBSITE_PAYMENT_LINK_PROVIDERS = new Set([JENNY_PAYMENT_LINK_PROVIDER, WEBSITE_PAYMENT_LINK_PROVIDER]);
const WEBSITE_PAYMENT_LINK_METHODS = new Set(['cashapp_via_lt', 'cashapp', 'applepay', 'googlepay', 'paypal', 'venmo', 'crypto']);
const JENNY_FIXED_AMOUNT_METHODS = new Set(['cashapp', 'applepay', 'googlepay']);
const PRESET_ONLY_GATEWAY_METHODS = new Set(['cashapp', 'applepay', 'googlepay']);
const JENNY_FIXED_GATEWAY_AMOUNTS = {
  cashapp: [
    9.99, 10.99, 11.99, 12.99, 13.99,
    14.99, 17.99, 19.99, 24.99, 29.99, 30.99, 39.99, 49.99, 59.99,
    99.99, 124.99, 129.99, 149.99, 199.99, 249.99, 299.99, 399.99,
    499.99,
  ],
  applepay: [
    9.99, 14.99, 17.99, 19.99, 24.99, 29.99, 30.99, 39.99, 49.99,
    59.99, 99.99, 124.99, 129.99, 149.99, 199.99, 300, 400, 500,
  ],
  googlepay: [
    9.99, 14.99, 17.99, 19.99, 24.99, 29.99, 30.99, 39.99, 49.99,
    59.99, 99.99, 124.99, 129.99, 149.99, 199.99, 300, 400, 500,
  ],
};
const DEFAULT_GATEWAY_AMOUNTS = {
  cashapp_via_lt: [
    4.99, 5.99, 6.99, 7.99, 8.99, 9.99, 10.99, 11.99, 12.99, 14.99,
    17.99, 19.99, 24.99, 29.99, 30.99, 39.99, 49.99, 59.99, 99.99,
    124.99, 129.99, 149.99, 199.99, 300.99, 400.99, 500.99,
  ],
  cashapp: [
    4.99, 5.99, 6.99, 7.99, 8.99, 9.99, 10.99, 11.99, 12.99, 14.99,
    17.99, 19.99, 24.99, 29.99, 30.99, 39.99, 49.99, 59.99, 99.99,
    124.99, 129.99, 149.99, 199.99, 300.99, 400.99, 500.99,
  ],
  applepay: [
    9.99, 14.99, 17.99, 19.99, 24.99, 29.99, 30.99, 39.99, 49.99, 59.99,
    99.99, 124.99, 129.99, 149.99, 199.99, 300.99, 400.99, 500.99,
  ],
  googlepay: [
    9.99, 14.99, 17.99, 19.99, 24.99, 29.99, 30.99, 39.99, 49.99, 59.99,
    99.99, 124.99, 129.99, 149.99, 199.99, 300.99, 400.99, 500.99,
  ],
};

const isApiGatewayMethod = (method) => {
  if (!method) return false;
  if ((method.payment_mode || '').toUpperCase() === 'GATEWAY') return true;

  const provider = String(
    method.gateway_provider
    || method.checkout_link_provider_code
    || method.provider_code
    || ''
  ).toLowerCase();
  if (provider && !['manual', 'local_manual', 'local'].includes(provider)) return true;

  const code = canonicalApiGatewayCode(method);
  if (!API_GATEWAY_CODES.has(code)) return false;

  const gatewayHints = [
    method.gateway_method,
    method.gateway_method_code,
    method.gateway_submethod,
    method.base_url,
    method.api_url,
    method.checkout_url,
    method.receiving_address,
    method.method_details,
    method.tags,
  ].join(' ').toLowerCase();

  return (
    gatewayHints.includes('checkout.1clickpay.vip')
    || gatewayHints.includes('1clickpay')
    || gatewayHints.includes('linkgen')
    || gatewayHints.includes('nowpayments')
    || gatewayHints.includes('paymentapi.vegaslobby.vip')
  );
};

const iframeCheckoutUrl = (payUrl, applePay) => {
  try {
    const parsed = new URL(payUrl);
    if (parsed.hostname.endsWith('nowpayments.io')) {
      parsed.protocol = 'https:';
      if (parsed.pathname === '/payment/') parsed.pathname = '/payment';
      return parsed.toString();
    }
    if (!applePay) return payUrl;
    if (parsed.hostname === 'checkout.nexmindglobal.com') {
      return `${window.location.origin}/__applepay-checkout${parsed.pathname}${parsed.search}`;
    }
  } catch {
    // Fall back to the gateway URL if the browser cannot parse it.
  }
  return payUrl;
};

const shouldUseExternalPaymentLink = (method) => {
  const code = canonicalApiGatewayCode(method);
  if (code === 'cashapp_via_lt' || code === 'cashapp' || code === 'applepay' || code === 'googlepay') {
    return true;
  }
  const mode = String(
    method?.player_checkout_mode
    || method?.checkout_mode
    || method?.checkout_presentation
    || ''
  ).toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (['externallink', 'paymentlink', 'website', 'link', 'browser'].includes(mode)) {
    return true;
  }
  if (['iframe', 'embed', 'embedded', 'inline'].includes(mode)) {
    return false;
  }
  return false;
};

const paymentLinkProviderCode = (method) => String(
  method?.gateway_provider
  || method?.checkout_link_provider_code
  || method?.provider_code
  || ''
).toLowerCase();

const isJennyPaymentLinkMethod = (method) => {
  if (!method) return false;
  const provider = paymentLinkProviderCode(method);
  return provider === JENNY_PAYMENT_LINK_PROVIDER
    && JENNY_PAYMENT_LINK_METHODS.has(canonicalApiGatewayCode(method));
};

const isWebsitePaymentLinkMethod = (method) => {
  if (!method) return false;
  const provider = paymentLinkProviderCode(method);
  return WEBSITE_PAYMENT_LINK_PROVIDERS.has(provider)
    && WEBSITE_PAYMENT_LINK_METHODS.has(canonicalApiGatewayCode(method));
};

const isJennyFixedAmountPaymentLinkMethod = (method) => (
  isJennyPaymentLinkMethod(method)
  && JENNY_FIXED_AMOUNT_METHODS.has(canonicalApiGatewayCode(method))
);

const isPresetOnlyGatewayAmountMethod = (method) => (
  PRESET_ONLY_GATEWAY_METHODS.has(canonicalApiGatewayCode(method))
);

const amountMatchesOptions = (value, options = []) => {
  const amountValue = Number(value);
  if (!Number.isFinite(amountValue)) return false;
  return options.some(option => Math.abs(amountValue - Number(option)) < 0.005);
};

const openPaymentLinkExternally = (url) => {
  const href = String(url || '').trim();
  if (!href) return;
  const opened = window.open(href, '_blank', 'noopener,noreferrer');
  if (!opened) {
    const anchor = document.createElement('a');
    anchor.href = href;
    anchor.target = '_blank';
    anchor.rel = 'noopener noreferrer';
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  }
};

const amountOptionsFor = (method) => {
  if (isJennyFixedAmountPaymentLinkMethod(method)) {
    return JENNY_FIXED_GATEWAY_AMOUNTS[canonicalApiGatewayCode(method)] || [];
  }
  const configured = (
    Array.isArray(method?.allowed_amounts) && method.allowed_amounts.length
      ? method.allowed_amounts
      : Array.isArray(method?.preset_amounts) && method.preset_amounts.length
        ? method.preset_amounts
        : []
  );
  const gatewayDefaults = isApiGatewayMethod(method)
    ? DEFAULT_GATEWAY_AMOUNTS[canonicalApiGatewayCode(method)] || []
    : [];
  const raw = configured.length ? configured : gatewayDefaults;
  return raw
    .map(v => Number(v))
    .filter(v => Number.isFinite(v) && v > 0)
    .map(v => Math.round(v * 100) / 100)
    .filter((v, idx, arr) => arr.findIndex(x => Math.abs(x - v) < 0.005) === idx)
    .sort((a, b) => a - b);
};

const paymentMethodDisplayCode = (method) => String(
  method?.gateway_method_code
  || method?.gateway_method
  || method?.method
  || method?.code
  || method?.title
  || ''
).toLowerCase().replace(/[^a-z0-9]+/g, '');

const defaultPaymentMethodOrder = (method) => {
  const code = paymentMethodDisplayCode(method);
  if (code === 'cashappvialt' || code === 'ecashapp' || code === 'ecash') return 1;
  if (code === 'cashapp') return 10;
  if (code === 'applepay') return 20;
  if (code === 'googlepay') return 30;
  if (code === 'chime') return 40;
  if (code === 'venmo') return 50;
  return 500;
};

const paymentMethodOrder = (method) => {
  const raw = Number(method?.display_order ?? method?.sort_order ?? method?.order);
  return Number.isFinite(raw) ? raw : defaultPaymentMethodOrder(method);
};

const sortPaymentMethodsForPlayer = (items = []) => [...items].sort((a, b) => {
  const orderDiff = paymentMethodOrder(a) - paymentMethodOrder(b);
  if (orderDiff !== 0) return orderDiff;
  const titleA = String(a?.title || a?.method || '').toLowerCase();
  const titleB = String(b?.title || b?.method || '').toLowerCase();
  return titleA.localeCompare(titleB);
});

const dedupePlayerGatewayMethods = (items = []) => {
  const selected = new Map();
  const passthrough = [];
  const uniqueMethods = new Map();
  const score = (method) => {
    const id = String(method?.id || '');
    return [
      id.startsWith('gateway-') ? 0 : 1,
      method?.logo_url || method?.qr_code_url || method?.qr_image_url ? 1 : 0,
      method?.player_badge_label || method?.tag_label || method?.display_tag ? 1 : 0,
      Array.isArray(method?.allowed_amounts) || Array.isArray(method?.preset_amounts) ? 1 : 0,
    ].join('');
  };

  items.filter(Boolean).forEach((method, index) => {
    const id = String(method?.id || '').trim();
    const flow = String(method?.flow || 'deposit').trim().toLowerCase();
    const fallbackKey = `${flow}:${paymentMethodDisplayCode(method)}:${String(method?.title || '').trim().toLowerCase()}`;
    uniqueMethods.set(id ? `id:${id}` : `method:${fallbackKey || index}`, method);
  });

  uniqueMethods.forEach((method) => {
    const code = canonicalPlayerGatewayKey(method);
    const flow = String(method?.flow || 'deposit').trim().toLowerCase();
    const key = API_GATEWAY_CODES.has(code) ? `${flow}:${code}` : '';
    if (!key) {
      passthrough.push(method);
      return;
    }
    const current = selected.get(key);
    if (!current || score(method) > score(current)) {
      selected.set(key, method);
    }
  });

  return sortPaymentMethodsForPlayer([...passthrough, ...selected.values()]);
};

function ManualPaymentDetails({ method, accent = '#22C55E', accentDark = '#15803D', showQr = true, beforeQr = null }) {
  if (!method) return null;
  const details = method.method_details || '';
  const address = String(method.receiving_address || '').trim();
  const tags = String(method.tags || '').trim();
  const note = method.admin_note || '';
  const qr = method.qr_image_url || method.qr_code_url || '';
  const walletName = address || 'Wallet tag name';
  const walletTag = tags || address;
  const noteLines = [details, note].filter(Boolean);
  const supportOnlyMethod = [
    method.title,
    method.method,
    method.player_badge_label,
    details,
    address,
    tags,
    note,
  ].some((value) => String(value || '').toLowerCase().includes('contact support'));
  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied!');
    } catch {
      toast.error('Could not copy');
    }
  };

  if (!details && !address && !tags && !note && !(showQr && qr)) return null;

  return (
    <div className="space-y-3">
      {walletTag && (
        <div
          className="rounded-2xl p-4"
          style={{ background: `${accent}10`, border: `1px solid ${accent}55` }}
          data-testid="manual-wallet-tag-card"
        >
          <p className="text-xs font-extrabold break-all" style={{ color: '#0B0F0C' }}>
            {walletName}
          </p>
          <div className="mt-2 flex items-center justify-between gap-3">
            <p className="text-xl font-extrabold mono break-all leading-tight" style={{ color: '#0B0F0C' }}>
              {walletTag}
            </p>
            <button
              type="button"
              onClick={() => copyText(walletTag)}
              className="shrink-0 p-2.5 rounded-xl"
              style={{ background: accent, color: '#fff' }}
              aria-label={`Copy ${walletName} tag`}
              data-testid="copy-manual-wallet-tag"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          {!supportOnlyMethod && (
            <p className="mt-2 flex items-start gap-1.5 text-[10px] sm:text-[11px] font-bold leading-snug" style={{ color: '#DC2626' }}>
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>Make sure to double check the tag before sending payment. Payment won&apos;t be loaded if you send on another tag.</span>
            </p>
          )}
        </div>
      )}
      {beforeQr}
      {showQr && qr && (
        <div className="flex justify-center">
          <SafeImage src={qr} alt={`${method.title || 'Payment'} QR`} className="w-44 h-44 rounded-xl bg-white object-contain" style={{ border: '1px solid rgba(0,0,0,0.1)' }} loading="lazy" fallback={null} />
        </div>
      )}
      {noteLines.length > 0 && (
        <div className="rounded-2xl p-4 text-sm" style={{ background: '#FFFBEB', border: '1px solid rgba(245,158,11,0.35)', color: '#92400E' }}>
          {noteLines.map((line, index) => (
            <p key={`${line}-${index}`} className={index ? 'mt-2' : ''}>{line}</p>
          ))}
        </div>
      )}
    </div>
  );
}

const isSupportContactMethod = (method) => {
  if (!method) return false;
  if (isApiGatewayMethod(method)) return false;
  const hasWalletDetails = String(method.receiving_address || method.tags || '').trim().length > 0;
  const hasQr = String(method.qr_image_url || method.qr_code_url || '').trim().length > 0;
  return !hasWalletDetails && !hasQr;
};

/* -------------------- WALLET FLOW (full-screen, multi-step) -------------------- */
export default function WalletFlow({ mode, setMode, step, setStep, method, setMethod, wallet, walletRules = {}, methods, transactions = [], refresh, checkAuth, close, branding = {}, onOpenSupport }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { tenantSlug } = useParams();
  const [balanceVisible, setBalanceVisible] = useState(true);
  const [amount, setAmount] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [payoutName, setPayoutName] = useState('');
  const [payoutDetails, setPayoutDetails] = useState('');
  const [tipAgent, setTipAgent] = useState(null);
  const [tipAmount, setTipAmount] = useState('');
  const [proofFile, setProofFile] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);
  const [checkoutPanel, setCheckoutPanel] = useState(null);
  const [latestSelectedMethod, setLatestSelectedMethod] = useState(null);
  const [transferPanelOpen, setTransferPanelOpen] = useState(false);
  const [fallbackMethods, setFallbackMethods] = useState([]);
  const [methodsLoading, setMethodsLoading] = useState(false);
  const [marketplaceEnabled, setMarketplaceEnabled] = useState(false);
  const proofInputRef = useRef(null);
  const submitLockRef = useRef(false);

  const normalizedMode = mode === 'withdrawal' ? 'withdraw' : mode;
  const slotsOceansScope = [
    tenantSlug,
    typeof window !== 'undefined' ? window.location.hostname : '',
  ].join(' ').toLowerCase().replace(/[^a-z0-9]+/g, '');
  const isSlotsOceansScope = ['slotsoceans', 'slotsocean', 'slotsocene']
    .some((alias) => slotsOceansScope.includes(alias));
  const parentMethods = useMemo(() => (Array.isArray(methods) ? methods.filter(Boolean) : []), [methods]);
  const flowMethods = useMemo(
    () => [...parentMethods, ...fallbackMethods]
      .filter((candidate) => methodMatchesFlow(candidate, normalizedMode)),
    [parentMethods, fallbackMethods, normalizedMode]
  );
  const filteredMethods = useMemo(
    () => dedupePlayerGatewayMethods(flowMethods).filter((candidate) => (
      normalizedMode !== 'deposit'
      || !isSlotsOceansScope
      || SLOTS_OCEANS_DEPOSIT_METHODS.has(canonicalPlayerGatewayKey(candidate))
    )),
    [flowMethods, isSlotsOceansScope, normalizedMode]
  );
  const activeMethod = latestSelectedMethod
    && method?.id
    && String(latestSelectedMethod.id) === String(method.id)
    ? latestSelectedMethod
    : method;
  const accent = mode === 'deposit' ? '#22C55E' : '#F97316';
  const accentDark = mode === 'deposit' ? '#15803D' : '#C2410C';
  const balanceUsd = Number(wallet?.cash_balance ?? wallet?.balance ?? 0);
  const freePlayBalance = Math.floor(Number(wallet?.play_credits ?? wallet?.fp_credits ?? wallet?.freeplay ?? 0));
  const marketplacePath = tenantSlug ? `/${tenantSlug}/app/explore/marketplace` : '/app/explore/marketplace';
  const transactionSignalKey = (txn) => {
    const text = [
      txn?.kind,
      txn?.type,
      txn?.request_type,
      txn?.direction,
      txn?.category,
      txn?.reason,
      txn?.description,
      txn?.payment_method,
      txn?.balance_type,
      txn?.ref_id,
      txn?.meta?.source,
      txn?.metadata?.source
    ].filter(Boolean).join(' ').toLowerCase();
    const rawAmount = txn?.amount ?? txn?.cash_amount ?? txn?.cash_delta ?? txn?.delta ?? txn?.value ?? txn?.total ?? 0;
    const amountKey = Math.abs(Number(rawAmount) || 0).toFixed(2);
    const rawDate = txn?.created_at || txn?.createdAt || txn?.timestamp || txn?.updated_at || txn?.date || '';
    const minuteKey = String(rawDate).slice(0, 16);
    if (text.includes('phone_verify') || text.includes('mobile_verification') || text.includes('phone verification')) {
      return `freeplay-phone-${amountKey}-${minuteKey}`;
    }
    if (text.includes('promo_credit') || text.includes('deposit_bonus') || text.includes('freeplay') || text.includes('free play')) {
      return `freeplay-${txn?.ref_id || txn?.id || txn?._id || amountKey}-${minuteKey}`;
    }
    // The backend timeline merges wallet_ledger + transactions + payment_requests,
    // so a single settled deposit/withdrawal can arrive twice with different ids.
    // Collapse those by their money-signature (direction + amount + minute +
    // status) instead of the id so the row shows once. A shared ref_id (order)
    // is the strongest signal when present.
    const statusKey = String(txn?.status || txn?.state || '').toLowerCase();
    if (/deposit|withdraw|cashout|cash_out|cashin|cash_in|payout/.test(text)) {
      const direction = /withdraw|cashout|cash_out|payout/.test(text) ? 'out' : 'in';
      if (txn?.ref_id) return `money-${direction}-ref-${txn.ref_id}`;
      return `money-${direction}-${amountKey}-${minuteKey}-${statusKey}`;
    }
    return txn?.id || txn?._id || `${text}-${amountKey}-${minuteKey}`;
  };

  const recentTransactions = useMemo(() => {
    const seen = new Set();
    return (Array.isArray(transactions) ? transactions.filter(Boolean) : [])
      .filter((txn) => {
        const key = transactionSignalKey(txn);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .slice(0, 5);
  }, [transactions]);
  const withdrawalRules = walletRules?.withdrawal_rules || {};
  const maxWithdrawalPerDay = Number(
    withdrawalRules.max_total_per_day ??
    withdrawalRules.max_daily_withdraw_total ??
    0
  ) || 0;
  const enteredAmount = Number(amount);
  const parsedTipAmount = Number(tipAmount);
  const enteredTipAmount = mode === 'withdrawal' && Number.isFinite(parsedTipAmount) && parsedTipAmount > 0
    ? Math.round(parsedTipAmount * 100) / 100
    : 0;
  const cashoutTipAmount = tipAgent ? enteredTipAmount : 0;
  const cashoutGrossAmount = mode === 'withdrawal' && Number.isFinite(enteredAmount) && enteredAmount > 0
    ? Math.round(enteredAmount * 100) / 100
    : 0;
  const cashoutNetAmount = mode === 'withdrawal'
    ? Math.max(Math.round((cashoutGrossAmount - cashoutTipAmount) * 100) / 100, 0)
    : cashoutGrossAmount;
  const hasTip = cashoutTipAmount > 0 && !!tipAgent;
  const exceedsDailyWithdrawalCap = mode === 'withdrawal'
    && maxWithdrawalPerDay > 0
    && cashoutGrossAmount > maxWithdrawalPerDay;
  const tipAgents = useMemo(() => (
    (Array.isArray(branding?.chat_agents) ? branding.chat_agents : [])
      .filter(agent => agent && agent.enabled !== false && (agent.name || agent.id))
  ), [branding?.chat_agents]);
  const resolveMediaUrl = (raw) => {
    const value = String(raw || '').trim();
    if (!value) return '';
    if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('data:')) return value;
    return `${process.env.REACT_APP_BACKEND_URL || ''}${value}`;
  };
  const brandLogoUrl = (() => {
    const raw = String(branding?.logo_url || branding?.intro_logo_url || '').trim();
    if (!raw) return '';
    if (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('data:')) return raw;
    return `${process.env.REACT_APP_BACKEND_URL || ''}${raw}`;
  })();

  const requestClose = () => {
    if (mode === 'deposit' && step === 2) {
      setStep(1);
      return;
    }
    if (step >= 2) {
      setConfirmCloseOpen(true);
    } else {
      close();
    }
  };

  const openSupportForLoad = () => {
    if (typeof onOpenSupport === 'function') {
      onOpenSupport();
      return;
    }
    toast.message('Open support from the chat button and our agents will help you load.');
  };

  useEffect(() => {
    let cancelled = false;
    api.get('/api/marketplace/items')
      .then((res) => {
        if (!cancelled) setMarketplaceEnabled(res.data?.enabled !== false);
      })
      .catch(() => {
        if (!cancelled) setMarketplaceEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, tenantSlug]);

  useEffect(() => {
    if (mode !== 'withdrawal') {
      setTipAgent(null);
      setTipAmount('');
    }
  }, [mode]);

  useEffect(() => {
    if (step !== 1) return undefined;

    let cancelled = false;
    const flow = normalizedMode === 'withdraw' ? 'withdraw' : 'deposit';
    // The wallet dashboard can stay mounted for a long session. Refresh the
    // canonical list whenever method selection opens so newly enabled methods
    // appear without requiring a full page reload.
    setMethodsLoading(true);
    api.get('/api/payment-methods', {
      params: { flow, _ts: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    })
      .then((res) => {
        if (cancelled) return;
        const list = res.data?.items || res.data?.methods || [];
        setFallbackMethods(Array.isArray(list) ? list.filter(Boolean) : []);
      })
      .catch(() => {
        if (!cancelled) setFallbackMethods([]);
      })
      .finally(() => {
        if (!cancelled) setMethodsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [step, normalizedMode]);

  useEffect(() => {
    if (!tipAgent) return;
    const selectedKey = String(tipAgent.id || tipAgent.name || '');
    const latest = tipAgents.find(agent => String(agent.id || agent.name || '') === selectedKey);
    if (!latest) {
      setTipAgent(null);
      setTipAmount('');
      return;
    }
    if (
      String(latest.name || '') !== String(tipAgent.name || '') ||
      String(latest.title || '') !== String(tipAgent.title || '') ||
      String(latest.avatar_url || '') !== String(tipAgent.avatar_url || '')
    ) {
      setTipAgent(latest);
    }
  }, [tipAgents, tipAgent]);

  useEffect(() => {
    const latestMethod = method?.id
      ? filteredMethods.find(item => item.id === method.id)
      : null;
    if (method?.id && (!latestMethod || latestMethod.player_enabled === false)) {
      setMethod(null);
    }
  }, [filteredMethods, method?.id, setMethod]);

  useEffect(() => {
    setWalletAddress('');
    setPayoutName('');
    setPayoutDetails('');
  }, [method?.id, mode]);

  useEffect(() => {
    if (!method?.id) {
      setLatestSelectedMethod(null);
      return undefined;
    }
    let cancelled = false;
    setLatestSelectedMethod(null);
    const flow = normalizedMode === 'withdraw' ? 'withdraw' : 'deposit';
    api.get(`/api/payment-methods/portal/${flow}`)
      .then(res => {
        if (cancelled) return;
        const list = res.data?.payment_methods || res.data?.methods || [];
        const fresh = list.find(item => String(item?.id) === String(method.id));
        if (fresh) {
          setLatestSelectedMethod({ ...method, ...fresh });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [method, method?.id, normalizedMode, step]);

  const stepperLabels = mode === 'withdrawal'
    ? ['Amount & Tip', 'Cashout Method', 'Transaction Details', 'Confirmation']
    : ['Payment method', 'Pay'];

  const transactionLabel = (txn) => {
    if (txn?.display_label || txn?.title) return txn.display_label || txn.title;
    const raw = String(txn?.kind || txn?.type || txn?.request_type || txn?.direction || txn?.category || '').toLowerCase();
    if (raw.includes('redeem') || raw.includes('cashout') || raw.includes('withdraw')) return 'Cashout';
    if (raw.includes('bonus') || raw.includes('freeplay') || raw.includes('fp') || raw.includes('reward') || raw.includes('promo')) return 'Free Play';
    if (raw.includes('load') || raw.includes('deposit') || raw.includes('payment')) return 'Deposit';
    return txn?.title || txn?.description || txn?.method || 'Wallet Update';
  };

  const transactionAmount = (txn) => {
    const raw = txn?.amount ?? txn?.cash_amount ?? txn?.cash_delta ?? txn?.delta ?? txn?.value ?? txn?.total;
    const amountValue = Number(raw);
    if (!Number.isFinite(amountValue)) return '';
    const label = transactionLabel(txn).toLowerCase();
    const signed = amountValue < 0 || label.includes('cashout') ? -Math.abs(amountValue) : Math.abs(amountValue);
    return `${signed < 0 ? '-' : '+'}$${Math.abs(signed).toFixed(2)}`;
  };

  const transactionStatus = (txn) => String(txn?.status || txn?.state || txn?.payment_status || 'pending').replace(/_/g, ' ');

  const transactionDate = (txn) => {
    const raw = txn?.created_at || txn?.createdAt || txn?.timestamp || txn?.updated_at || txn?.date;
    if (!raw) return '';
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  };

  const openWalletAction = (nextMode) => {
    setMode(nextMode);
    setMethod(null);
    setAmount('');
    setProofFile(null);
    setWalletAddress('');
    setPayoutName('');
    setPayoutDetails('');
    setTipAgent(null);
    setTipAmount('');
    setStep(1);
  };

  const submit = async () => {
    if (submitLockRef.current || processing) {
      return;
    }
    submitLockRef.current = true;
    const selectedAmount = mode === 'withdrawal' ? cashoutNetAmount : Number(amount);
    if (mode === 'withdrawal') {
      if (!cashoutGrossAmount || cashoutGrossAmount <= 0) { toast.error('Enter cashout amount'); return; }
      if (exceedsDailyWithdrawalCap) { toast.error(`Maximum withdrawal per day is ${moneyLabel(maxWithdrawalPerDay)}`); return; }
      if (cashoutTipAmount >= cashoutGrossAmount) { toast.error('Tip must be lower than the cashout amount'); return; }
      if (!Number.isFinite(selectedAmount) || selectedAmount <= 0) { toast.error('Withdrawal amount must be greater than $0'); return; }
    } else if (!Number.isFinite(selectedAmount) || selectedAmount <= 0) {
      toast.error('Select a load amount');
      return;
    }
    if (!method) { toast.error('Pick a payment method'); return; }
    setProcessing(true);
    try {
      if (mode === 'deposit') {
        const selectedMethod = activeMethod || method;
        const isGateway = isApiGatewayMethod(selectedMethod);
        if (!isGateway && !proofFile) {
          toast.error('Upload payment screenshot before submitting');
          setProcessing(false);
          return;
        }
        if (isGateway && isPresetOnlyGatewayAmountMethod(selectedMethod)) {
          const allowedOptions = amountOptionsFor(selectedMethod);
          if (!allowedOptions.length || !amountMatchesOptions(selectedAmount, allowedOptions)) {
            toast.error('Pick one of the listed load amounts');
            setProcessing(false);
            return;
          }
        }
        if (!isGateway && isWebsitePaymentLinkMethod(selectedMethod)) {
          const paymentMethod = canonicalApiGatewayCode(selectedMethod);
          setCheckoutPanel({
            loading: true,
            url: '',
            originalUrl: '',
            orderId: null,
            amount: selectedAmount,
            methodTitle: selectedMethod.title,
            applePay: false,
            linkReady: true,
          });
          const res = await api.post('/api/create-link', {
            amount: selectedAmount,
            method: paymentMethod,
          });
          const payUrl = (
            res.data?.url
            || res.data?.pay_url
            || res.data?.payment_url
            || res.data?.payment_link
            || res.data?.checkout_url
            || res.data?.checkoutUrl
            || res.data?.link
          );
          if (!res.data?.ok || !payUrl) {
            const message = res.data?.error || 'Payment link could not be created. Please try again.';
            toast.error(message);
            setCheckoutPanel(prev => (prev ? { ...prev, loading: false, failed: true, errorMessage: message } : prev));
            setProcessing(false);
            return;
          }
          toast.success('Payment link ready');
          setCheckoutPanel(prev => ({
            ...(prev || {}),
            loading: false,
            linkReady: true,
            url: payUrl,
            originalUrl: payUrl,
            orderId: res.data?.reqId || null,
            amount: res.data?.amount || selectedAmount,
            methodTitle: selectedMethod.title,
            applePay: false,
          }));
          window.location.assign(payUrl);
          return;
        }
        if (isGateway) {
          const shouldTreatAsApplePay = isApplePayGateway(selectedMethod);
          const useExternalPaymentLink = shouldUseExternalPaymentLink(selectedMethod);
          const idem = `gw-deposit-${user?.id || 'anon'}-${Date.now()}`;
          const gatewayMethod = canonicalApiGatewayCode(selectedMethod);
          const gatewaySubmethod = canonicalApiGatewayCode(selectedMethod) || gatewaySubmethodCode(selectedMethod);
          setCheckoutPanel({
            loading: true,
            url: '',
            originalUrl: '',
            orderId: null,
            amount: selectedAmount,
            methodTitle: selectedMethod.title,
            applePay: shouldTreatAsApplePay,
            linkReady: useExternalPaymentLink,
          });
          const res = await api.post(
            '/api/gateway/deposit',
            {
              amount: selectedAmount,
              method: gatewayMethod,
              currency: 'USD',
              gateway_submethod: gatewaySubmethodCode(selectedMethod) || gatewaySubmethod,
            },
            { headers: { 'X-Idempotency-Key': idem } }
          );
          const payUrl = (
            res.data?.pay_url
            || res.data?.payment_url
            || res.data?.payment_link
            || res.data?.checkout_url
            || res.data?.checkoutUrl
            || res.data?.url
            || res.data?.link
          );
          if (!payUrl) {
            const message = 'Gateway did not return a payment URL';
            toast.error(message);
            setCheckoutPanel(prev => (prev ? { ...prev, loading: false, failed: true, errorMessage: message } : prev));
            setProcessing(false);
            return;
          }
          toast.success(useExternalPaymentLink ? 'Payment link ready' : 'Opening secure checkout...');
          setCheckoutPanel(prev => ({
            ...(prev || {}),
            loading: false,
            linkReady: useExternalPaymentLink,
            url: useExternalPaymentLink ? payUrl : iframeCheckoutUrl(payUrl, shouldTreatAsApplePay),
            originalUrl: payUrl,
            orderId: res.data?.order_id,
            amount: res.data?.charged_amount || selectedAmount,
            methodTitle: selectedMethod.title,
            applePay: shouldTreatAsApplePay,
          }));
          return;
        }
        const manualDepositIdem = `manual-deposit-${user?.id || 'anon'}-${Date.now()}`;
        const res = await api.post(
          '/api/requests/deposit',
          { amount: selectedAmount, payment_method_id: method.id },
          { headers: { 'X-Idempotency-Key': manualDepositIdem } }
        );
        const txnId = res.data.id;
        if (proofFile && txnId) {
          const fd = new FormData();
          fd.append('file', proofFile);
          await api.post(`/api/requests/deposit/${txnId}/upload-proof`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
        toast.success('Purchase request submitted!');
      } else {
        const labels = cashoutMethodLabels(activeMethod || method);
        const cleanPayoutName = String(payoutName || '').trim();
        const cleanPayoutTag = String(walletAddress || '').trim();
        if (!cleanPayoutName || !cleanPayoutTag) {
          toast.error(`Enter ${labels.nameLabel} and ${labels.tagLabel}`);
          setProcessing(false);
          return;
        }
        const normalizedPayoutDetails = {
          method_label: labels.methodName,
          label_name: labels.nameLabel,
          label_tag: labels.tagLabel,
          account_name: cleanPayoutName,
          payout_name: cleanPayoutName,
          name: cleanPayoutName,
          account_tag: cleanPayoutTag,
          payout_tag: cleanPayoutTag,
          tag: cleanPayoutTag,
          address: cleanPayoutTag,
          wallet_address: cleanPayoutTag,
        };
        const cleanNote = String(payoutDetails || '').trim();
        if (cleanNote) {
          normalizedPayoutDetails.details = cleanNote;
          normalizedPayoutDetails.note = cleanNote;
        }
        await api.post('/api/requests/withdraw', {
          amount: selectedAmount, payment_method_id: method.id,
          payout_details: normalizedPayoutDetails,
          tip_amount: hasTip ? cashoutTipAmount : 0,
          tip_agent_id: hasTip ? (tipAgent?.id || '') : '',
          tip_agent_name: hasTip ? (tipAgent?.name || '') : '',
          tip_agent_title: hasTip ? (tipAgent?.title || '') : '',
          tip_agent_avatar_url: hasTip ? (tipAgent?.avatar_url || '') : '',
        });
        toast.success('Cashout request submitted!');
        await checkAuth();
      }
      refresh();
      close();
    } catch (err) {
      const message = isApiGatewayMethod(activeMethod || method)
        ? 'Payment link could not be created. Please try again.'
        : (err.response?.data?.detail || 'Request failed');
      toast.error(message);
      if (isApiGatewayMethod(activeMethod || method)) {
        setCheckoutPanel(prev => (prev ? { ...prev, loading: false, failed: true, errorMessage: message } : prev));
      }
    } finally {
      submitLockRef.current = false;
      setProcessing(false);
    }
  };

  useEffect(() => {
    if (!checkoutPanel?.orderId || checkoutPanel?.verified || checkoutPanel?.failed) return undefined;
    let stopped = false;
    let tries = 0;
    const maxTries = 180;

    const poll = async () => {
      if (stopped) return;
      tries += 1;
      try {
        const res = await api.post(`/api/deposits/${checkoutPanel.orderId}/poll-status`);
        const status = String(res.data?.status || res.data?.gateway_status || '').toUpperCase();
        if (['APPROVED', 'SUCCESS', 'PAID', 'COMPLETED', 'SETTLED'].includes(status)) {
          toast.success('Payment verified. Wallet updated.');
          await checkAuth?.();
          refresh?.();
          setCheckoutPanel(prev => (prev ? { ...prev, verified: true } : prev));
          return;
        }
        if (['FAILED', 'REJECTED', 'CANCELLED', 'CANCELED', 'EXPIRED', 'TIMEOUT', 'TIMED_OUT'].includes(status)) {
          const expired = ['EXPIRED', 'TIMEOUT', 'TIMED_OUT'].includes(status);
          toast.error(expired ? 'Payment link expired. Please create a new link.' : 'Payment was not completed.');
          setCheckoutPanel(prev => (prev ? {
            ...prev,
            failed: true,
            errorMessage: expired ? 'Payment link expired. Please create a new link.' : prev.errorMessage,
          } : prev));
          return;
        }
      } catch {
        // Keep polling; website providers can take a few seconds to settle.
      }
      if (!stopped && tries < maxTries) {
        window.setTimeout(poll, tries < 5 ? 2500 : 5000);
      } else if (!stopped) {
        toast.error('Payment link expired. Please create a new link.');
        setCheckoutPanel(prev => (prev ? {
          ...prev,
          failed: true,
          errorMessage: 'Payment link expired. Please create a new link.',
        } : prev));
      }
    };

    const timer = window.setTimeout(poll, 2500);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
    };
  }, [checkoutPanel, checkAuth, close, refresh]);

  const moneyLabel = (value) => `$${Number(value || 0).toFixed(2)}`;
  const validateWithdrawalAmountStep = () => {
    if (!cashoutGrossAmount || cashoutGrossAmount <= 0) {
      toast.error('Confirm your cashout amount');
      return false;
    }
    if (exceedsDailyWithdrawalCap) {
      toast.error(`Maximum withdrawal per day is ${moneyLabel(maxWithdrawalPerDay)}`);
      return false;
    }
    if (cashoutTipAmount >= cashoutGrossAmount) {
      toast.error('Tip must be lower than the cashout amount');
      return false;
    }
    if (cashoutNetAmount <= 0) {
      toast.error('Withdrawal amount must be greater than $0');
      return false;
    }
    return true;
  };

  const renderMethodList = (intro = 'Pick a payment method to continue.') => (
    <div className="space-y-3" data-testid="wallet-step-method">
      <p className="text-sm" style={{ color: '#6B7280' }}>{intro}</p>
      {filteredMethods.length === 0 ? (
        <div className="rounded-2xl p-8 text-center" style={{ background: '#F3F4F6' }}>
          {methodsLoading ? (
            <>
              <RefreshCw className="mx-auto mb-3 h-5 w-5 animate-spin" style={{ color: accent }} />
              <p className="font-bold" style={{ color: '#0B0F0C' }}>Loading payment methods</p>
              <p className="text-sm mt-1" style={{ color: '#6B7280' }}>Checking available options...</p>
            </>
          ) : (
            <>
              <p className="font-bold" style={{ color: '#0B0F0C' }}>No methods available</p>
              <p className="text-sm mt-1" style={{ color: '#6B7280' }}>Please contact support.</p>
            </>
          )}
        </div>
      ) : filteredMethods.map(m => {
        const selected = method?.id === m.id;
        const playerEnabled = m.player_enabled !== false;
        const badgeLabel = paymentMethodBadgeLabel(m, mode);
        const badgeStyle = paymentBadgeStyle(m.player_badge_tone, accent);
        const finePrint = depositMethodFinePrint(m, mode);
        return (
          <div key={m.id}>
            <button
              type="button"
              onClick={() => playerEnabled && setMethod(m)}
              disabled={!playerEnabled}
              className="w-full p-4 rounded-2xl flex items-center gap-4 transition active:scale-[0.99] relative overflow-hidden disabled:cursor-not-allowed disabled:active:scale-100"
              style={{
                background: selected ? `${accent}10` : '#F8FAFC',
                border: selected ? `2px solid ${accent}` : '1px solid rgba(0,0,0,0.08)',
                boxShadow: selected ? `0 6px 18px ${accent}33` : 'none',
                opacity: playerEnabled ? 1 : 0.48,
                filter: playerEnabled ? 'none' : 'grayscale(0.35)',
              }}
              aria-disabled={!playerEnabled}
              data-testid={`fs-pay-method-${m.id}`}
            >
              <PaymentMethodLogo method={m} accent={accent} accentDark={accentDark} />
              <div className="min-w-0 flex-1 text-left">
                <p className="display break-words text-base font-bold" style={{ color: '#0B0F0C' }}>{m.title}</p>
                <div className="flex gap-2 mt-0.5 flex-wrap">
                  {m.bonus_percent > 0 && mode === 'deposit' && (
                    <span className="text-[11px] font-bold" style={{ color: '#15803D' }}>+{m.bonus_percent}% bonus</span>
                  )}
                  {finePrint && (
                    <span
                      className="text-[11px] font-bold"
                      style={{ color: finePrint === 'No fee' ? '#10B981' : (finePrint === '2% fee' ? '#F59E0B' : '#6B7280') }}
                    >
                      {finePrint}
                    </span>
                  )}
                </div>
              </div>
              <div className="ml-auto flex max-w-[92px] shrink-0 flex-col items-end gap-2 min-[380px]:max-w-[118px]">
                {badgeLabel && (
                  <span
                    className="payment-method-hot-badge max-w-full"
                    style={{ ...badgeStyle, maxWidth: '100%' }}
                  >
                    <Sparkles className="h-3 w-3" />
                    {badgeLabel}
                  </span>
                )}
                {selected ? (
                  <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: accent }}>
                    <Check className="w-4 h-4 text-white" strokeWidth={4} />
                  </div>
                ) : (
                  <ChevronDown className="w-4 h-4 -rotate-90" style={{ color: '#9CA3AF' }} />
                )}
              </div>
            </button>
            {!playerEnabled && (
              <p className="mt-1.5 px-2 text-left text-xs font-semibold" style={{ color: '#B45309' }}>
                {m.disabled_message || 'Please contact support to enable it'}
              </p>
            )}
          </div>
        );
      })}
    </div>
  );

  const renderWithdrawalAmountStep = () => {
    const quickAmounts = [10, 25, 50, 100, 250];
    const tipOptions = [5, 10, 25, 50];
    return (
      <div className="space-y-5" data-testid="wallet-step-withdrawal-amount">
        <div className="rounded-2xl p-5" style={{ background: '#FFF7ED', border: `1px solid ${accent}55` }}>
          <p className="display text-lg font-extrabold" style={{ color: '#0B0F0C' }}>Amount to withdraw</p>
          <p className="text-xs mt-1" style={{ color: '#6B7280' }}>Confirm the wallet amount first, then add an optional agent tip below.</p>
          {maxWithdrawalPerDay > 0 && (
            <div
              className="mt-3 rounded-xl px-3 py-2 text-xs font-extrabold"
              style={{
                background: exceedsDailyWithdrawalCap ? '#FEF2F2' : '#FFFBEB',
                border: `1px solid ${exceedsDailyWithdrawalCap ? 'rgba(239,68,68,0.35)' : 'rgba(245,158,11,0.35)'}`,
                color: exceedsDailyWithdrawalCap ? '#991B1B' : '#92400E',
              }}
              data-testid="withdrawal-daily-cap"
            >
              Max withdrawal per day: {moneyLabel(maxWithdrawalPerDay)}
            </div>
          )}
          <Input
            type="number"
            min="0"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            placeholder="0.00"
            className="h-14 text-2xl font-bold mono mt-4"
            style={{ background: '#FFFFFF', border: `2px solid ${accent}55`, color: '#0B0F0C' }}
            data-testid="fs-wallet-amount"
            autoFocus
          />
          <div className="grid grid-cols-5 gap-2 mt-3">
            {quickAmounts.map(v => {
              const selected = Number(amount) === v;
              return (
                <button
                  key={v}
                  type="button"
                  onClick={() => setAmount(String(v))}
                  className="py-2 rounded-xl text-sm font-bold display transition"
                  style={{
                    background: selected ? accent : '#F3F4F6',
                    color: selected ? '#FFFFFF' : '#0B0F0C',
                  }}
                >
                  ${v}
                </button>
              );
            })}
          </div>
        </div>

        <div className="rounded-2xl p-5 space-y-4" style={{ background: '#F8FAFC', border: '1px solid rgba(0,0,0,0.08)' }}>
          <div>
            <p className="display text-lg font-extrabold" style={{ color: '#0B0F0C' }}>Optional tip to your favorite agent</p>
            <p className="text-xs mt-1" style={{ color: '#6B7280' }}>Optional. Pick an agent first, then choose a tip.</p>
          </div>

          {tipAgents.length === 0 ? (
            <div className="rounded-xl p-4 text-sm" style={{ background: '#FFFFFF', color: '#6B7280', border: '1px dashed rgba(0,0,0,0.14)' }}>
              No agents available for tips right now.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {tipAgents.map(agent => {
                const selected = String(tipAgent?.id || tipAgent?.name) === String(agent.id || agent.name);
                const avatar = resolveMediaUrl(agent.avatar_url);
                return (
                  <button
                    key={agent.id || agent.name}
                    type="button"
                    onClick={() => {
                      setTipAgent(agent);
                      if (!tipAmount) setTipAmount('5');
                    }}
                    className="p-3 rounded-2xl flex items-center gap-3 text-left transition active:scale-[0.99]"
                    style={{
                      background: selected ? `${accent}12` : '#FFFFFF',
                      border: selected ? `2px solid ${accent}` : '1px solid rgba(0,0,0,0.08)',
                      boxShadow: selected ? `0 8px 18px ${accent}25` : 'none',
                    }}
                  >
                    <span className="w-12 h-12 rounded-full overflow-hidden shrink-0 flex items-center justify-center" style={{ background: `${accent}18`, color: accentDark }}>
                      {avatar ? (
                        <SafeImage
                          src={avatar}
                          alt={agent.name || 'Agent'}
                          className="w-full h-full object-cover"
                          loading="lazy"
                          fallback={<UserRound className="w-6 h-6" />}
                        />
                      ) : (
                        <UserRound className="w-6 h-6" />
                      )}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="display font-extrabold block truncate" style={{ color: '#0B0F0C' }}>{agent.name || 'Agent'}</span>
                      <span className="text-xs block truncate" style={{ color: '#6B7280' }}>{agent.title || 'Support Agent'}</span>
                    </span>
                    {selected && <CheckCircle2 className="w-5 h-5 shrink-0" style={{ color: accentDark }} />}
                  </button>
                );
              })}
            </div>
          )}

          {tipAgent && (
            <div className="space-y-3">
              <div className="grid grid-cols-4 gap-2">
                {tipOptions.map(v => {
                  const selected = Number(tipAmount) === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      onClick={() => setTipAmount(String(v))}
                      className="py-2 rounded-xl text-sm font-bold display transition"
                      style={{
                        background: selected ? accent : '#EEF2F7',
                        color: selected ? '#FFFFFF' : '#0B0F0C',
                      }}
                    >
                      ${v}
                    </button>
                  );
                })}
              </div>
              <Input
                type="number"
                min="0"
                value={tipAmount}
                onChange={e => setTipAmount(e.target.value)}
                placeholder="Custom tip amount"
                className="h-12"
                style={{ background: '#FFFFFF', border: '1px solid rgba(0,0,0,0.12)', color: '#0B0F0C' }}
                data-testid="wallet-tip-amount"
              />
              <button
                type="button"
                onClick={() => { setTipAgent(null); setTipAmount(''); }}
                className="text-xs display font-bold"
                style={{ color: '#6B7280' }}
              >
                Remove tip
              </button>
            </div>
          )}
        </div>

        <div className="rounded-2xl p-5 space-y-2 text-sm" style={{ background: '#FFF7ED', border: `1px solid ${accent}55` }}>
          <Row label="Wallet deduction" value={moneyLabel(cashoutGrossAmount)} valueBold />
          {hasTip && <Row label={`Tip to ${tipAgent?.name || 'agent'}`} value={moneyLabel(cashoutTipAmount)} valueBold />}
          <Row label="Withdrawal amount" value={moneyLabel(cashoutNetAmount)} valueBold />
          {maxWithdrawalPerDay > 0 && <Row label="Daily max" value={moneyLabel(maxWithdrawalPerDay)} valueBold={exceedsDailyWithdrawalCap} />}
        </div>
      </div>
    );
  };

  const renderDepositAmountStep = () => {
    const displayMethod = activeMethod || method;
    const supportContactMethod = isSupportContactMethod(displayMethod);
    const depositAmountValue = Number(amount);
    const cashAppGatewaySelected = canonicalApiGatewayCode(displayMethod) === 'cashapp';
    const cashAppFeeAmount = cashAppGatewaySelected && Number.isFinite(depositAmountValue) && depositAmountValue > 0
      ? Math.round(depositAmountValue * 2) / 100
      : 0;
    const cashAppWalletCreditAmount = Math.max(
      Math.round((depositAmountValue - cashAppFeeAmount) * 100) / 100,
      0
    );
    return (
      <div className="space-y-5" data-testid="wallet-step-amount">
        <div className="rounded-2xl p-4 flex items-center gap-3" style={{ background: '#F8FAFC', border: '1px solid rgba(0,0,0,0.08)' }}>
          <PaymentMethodLogo method={displayMethod} accent={accent} accentDark={accentDark} size="sm" />
          <div className="flex-1">
            <p className="text-[10px] uppercase tracking-wider" style={{ color: '#6B7280' }}>Method</p>
            <p className="display font-bold" style={{ color: '#0B0F0C' }}>{displayMethod.title}</p>
          </div>
          <button onClick={() => setStep(1)} className="text-xs font-bold display" style={{ color: accentDark }}>Change</button>
        </div>

        {supportContactMethod ? (
          <div className="rounded-2xl p-5 space-y-4" style={{ background: '#F0FDF4', border: '1px solid rgba(34,197,94,0.35)' }}>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] font-extrabold" style={{ color: '#15803D' }}>
                Contact support for load
              </p>
              <p className="display text-xl font-extrabold mt-2" style={{ color: '#0B0F0C' }}>
                Our agent will help you complete this load.
              </p>
            </div>
            <ManualPaymentDetails method={displayMethod} accent={accent} accentDark={accentDark} showQr={false} />
            <button
              type="button"
              onClick={openSupportForLoad}
              className="w-full h-12 rounded-xl display font-extrabold"
              style={{ background: 'linear-gradient(180deg,#22C55E,#15803D)', color: '#FFFFFF', boxShadow: '0 8px 22px rgba(34,197,94,0.35)' }}
              data-testid="wallet-open-support-load"
            >
              Open Support →
            </button>
          </div>
        ) : (() => {
          const isGateway = isApiGatewayMethod(displayMethod);
          const quickAmounts = amountOptionsFor(displayMethod);
          const presetOnlyGatewayAmount = isPresetOnlyGatewayAmountMethod(displayMethod);
          const gatewayAllowsCustomAmount = isGateway && (
            !presetOnlyGatewayAmount
            && !isJennyFixedAmountPaymentLinkMethod(displayMethod)
            && (displayMethod?.allow_custom_amount === true
            || quickAmounts.length === 0
            || canonicalApiGatewayCode(displayMethod) === 'crypto')
          );
          const showCustomInput = !isGateway || gatewayAllowsCustomAmount;
          return (
            <div>
              <label className="text-xs uppercase tracking-wider mb-2 block font-bold" style={{ color: '#6B7280' }}>
                {isGateway && quickAmounts.length && !gatewayAllowsCustomAmount ? 'Select Load Amount' : 'Amount ($)'}
              </label>
              {showCustomInput && (
                <Input
                  type="number"
                  value={amount}
                  onChange={e => setAmount(e.target.value)}
                  placeholder="0.00"
                  className="h-14 text-2xl font-bold mono"
                  style={{ background: '#fff', border: `2px solid ${accent}55`, color: '#0B0F0C' }}
                  data-testid="fs-wallet-amount"
                  autoFocus
                />
              )}
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mt-3">
                {(quickAmounts.length ? quickAmounts : (isGateway ? [] : [5, 10, 25, 50, 100])).map(v => {
                  const display = Number.isInteger(Number(v)) ? `$${v}` : `$${Number(v).toFixed(2)}`;
                  const valStr = String(v);
                  const selected = Number(amount) === Number(v);
                  return (
                    <button
                      key={valStr}
                      onClick={() => setAmount(valStr)}
                      disabled={processing}
                      className="py-2 rounded-xl text-sm font-bold display transition disabled:opacity-60"
                      style={{
                        background: selected ? accent : '#F3F4F6',
                        color: selected ? '#fff' : '#0B0F0C'
                      }}
                      data-testid={`quick-${valStr}`}
                    >
                      {processing && selected ? '...' : display}
                    </button>
                  );
                })}
              </div>
              {isGateway && quickAmounts.length > 0 && !gatewayAllowsCustomAmount && (
                <p className="text-xs mt-3" style={{ color: '#6B7280' }}>
                  Select an amount, then continue to checkout.
                </p>
              )}
              {isGateway && quickAmounts.length > 0 && gatewayAllowsCustomAmount && (
                <p className="text-xs mt-3" style={{ color: '#6B7280' }}>
                  Enter a custom amount or use a quick amount, then continue to checkout.
                </p>
              )}
              {isGateway && quickAmounts.length === 0 && !gatewayAllowsCustomAmount && (
                <div className="rounded-2xl p-4 text-center" style={{ background: '#F3F4F6' }}>
                  <p className="font-bold" style={{ color: '#0B0F0C' }}>No load amounts configured</p>
                  <p className="text-sm mt-1" style={{ color: '#6B7280' }}>Please contact support.</p>
                </div>
              )}
            </div>
          );
	        })()}

        {mode === 'deposit' && cashAppFeeAmount > 0 && !supportContactMethod && (
          <div
            className="rounded-2xl p-4 space-y-2 text-sm"
            style={{ background: '#FFF7ED', border: '1px solid rgba(245,158,11,0.42)' }}
            data-testid="cashapp-fee-summary"
          >
            <Row label="Payment amount" value={moneyLabel(depositAmountValue)} valueBold />
            <Row label="Cash App fee (2%)" value={moneyLabel(cashAppFeeAmount)} valueBold />
            <Row label="Wallet credit" value={moneyLabel(cashAppWalletCreditAmount)} valueBold />
          </div>
        )}

        {mode === 'deposit' && displayMethod && !supportContactMethod && (() => {
          const isGateway = isApiGatewayMethod(displayMethod);
          if (isGateway) {
            return (
              <div
                className="rounded-2xl p-4 flex items-start gap-3"
                style={{ background: '#F0FDF4', border: '1px solid rgba(34,197,94,0.4)' }}
                data-testid="gateway-instant-notice"
              >
                <span
                  className="w-9 h-9 rounded-full shrink-0 flex items-center justify-center"
                  style={{ background: '#22C55E', boxShadow: '0 0 12px rgba(34,197,94,0.55)' }}
                >
                  <Sparkles className="w-5 h-5 text-white" />
                </span>
                <div className="text-sm leading-snug" style={{ color: '#0F2D1A' }}>
                  <p className="font-extrabold display">Instant secure checkout</p>
                  <p className="mt-1" style={{ color: '#3F6D52' }}>
                    Your secure {displayMethod.title} checkout opens from a payment link. Your wallet
                    is credited automatically the moment payment is verified — no screenshot needed.
                  </p>
                </div>
              </div>
            );
          }
          return (
            <div className="space-y-3">
              <ManualPaymentDetails
                method={displayMethod}
                accent={accent}
                accentDark={accentDark}
                showQr
                beforeQr={(
                  <>
                    <input type="file" ref={proofInputRef} accept="image/*" className="hidden" onChange={e => setProofFile(e.target.files[0])} />
                    <div onClick={() => proofInputRef.current?.click()} className="border-2 border-dashed rounded-2xl p-4 text-center cursor-pointer" style={{ borderColor: 'rgba(34,197,94,0.4)', background: '#F0FDF4' }}>
                      {proofFile ? (
                        <div className="flex items-center justify-center gap-2 text-sm">
                          <CheckCircle2 className="w-4 h-4" style={{ color: '#22C55E' }} />
                          <span className="font-bold" style={{ color: '#0B0F0C' }}>{proofFile.name}</span>
                          <button onClick={(e) => { e.stopPropagation(); setProofFile(null); }}><XCircle className="w-4 h-4" style={{ color: '#9CA3AF' }} /></button>
                        </div>
                      ) : (
                        <p className="text-sm font-bold" style={{ color: '#15803D' }}>Click to upload payment screenshot (required)</p>
                      )}
                    </div>
                  </>
                )}
              />
            </div>
          );
        })()}
      </div>
    );
  };

  const renderSelectedCashoutMethod = () => {
    if (!method) return null;
    return (
      <div
        className="w-full p-4 rounded-2xl flex items-center gap-4"
        style={{
          background: `${accent}08`,
          border: `2px solid ${accent}`,
          boxShadow: `0 8px 22px ${accent}22`,
        }}
        data-testid="wallet-selected-cashout-method"
      >
        <PaymentMethodLogo method={method} accent={accent} accentDark={accentDark} />
        <div className="flex-1 text-left">
          <p className="display text-base font-bold" style={{ color: '#0B0F0C' }}>{method.title}</p>
          {method.min_amount && (<p className="text-xs mt-0.5" style={{ color: '#6B7280' }}>Min ${method.min_amount}</p>)}
        </div>
        <div className="w-7 h-7 rounded-full flex items-center justify-center" style={{ background: accent }}>
          <Check className="w-4 h-4 text-white" strokeWidth={4} />
        </div>
      </div>
    );
  };

  const renderWithdrawalSummary = () => (
    <div className="rounded-2xl p-4 text-sm space-y-2" style={{ background: '#FFF7ED', border: `1px solid ${accent}55` }}>
      <Row label="Confirmed amount" value={moneyLabel(cashoutGrossAmount)} valueBold />
      {hasTip && <Row label={`Tip to ${tipAgent?.name || 'agent'}`} value={moneyLabel(cashoutTipAmount)} valueBold />}
      <Row label="Withdrawal amount" value={moneyLabel(cashoutNetAmount)} valueBold />
    </div>
  );

  const renderWithdrawalMethodStep = () => (
    <div className="space-y-5" data-testid="wallet-step-withdrawal-method">
      {renderWithdrawalSummary()}
      {renderMethodList('Pick a cashout method to continue.')}
    </div>
  );

  const renderWithdrawalDetailsStep = () => {
    const labels = cashoutMethodLabels(activeMethod || method);
    return (
      <div className="space-y-5" data-testid="wallet-step-withdrawal-details">
        {renderWithdrawalSummary()}
        {method && (
          <div className="space-y-3">
            {renderSelectedCashoutMethod()}
            <ManualPaymentDetails method={method} accent={accent} accentDark={accentDark} showQr />
            <Input
              value={payoutName}
              onChange={e => setPayoutName(e.target.value)}
              placeholder={labels.namePlaceholder}
              className="h-12"
              style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.12)', color: '#0B0F0C' }}
              data-testid="fs-payout-name"
            />
            <Input
              value={walletAddress}
              onChange={e => setWalletAddress(e.target.value)}
              placeholder={labels.tagPlaceholder}
              className="h-12"
              style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.12)', color: '#0B0F0C' }}
              data-testid="fs-wallet-address"
            />
            <div
              className="rounded-2xl p-3 text-xs font-bold leading-snug flex items-start gap-2"
              style={{ background: '#FEF2F2', border: '1px solid rgba(239,68,68,0.35)', color: '#991B1B' }}
              data-testid="cashout-detail-warning"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Enter your correct {labels.nameLabel} and {labels.tagLabel}. If wrong cashout details are submitted, we are not responsible for failed or delayed payout.
              </span>
            </div>
            <Input
              value={payoutDetails}
              onChange={e => setPayoutDetails(e.target.value)}
              placeholder="Additional details (optional)"
              className="h-12"
              style={{ background: '#fff', border: '1px solid rgba(0,0,0,0.12)', color: '#0B0F0C' }}
            />
          </div>
        )}
      </div>
    );
  };

  const renderWithdrawalReviewStep = () => {
    const labels = cashoutMethodLabels(activeMethod || method);
    return (
      <div className="space-y-5" data-testid="wallet-step-confirm">
        <div className="rounded-2xl p-5" style={{ background: '#FFF7ED', border: `1px solid ${accent}55` }}>
          <p className="display font-extrabold text-lg mb-3" style={{ color: '#0B0F0C' }}>
            Review your cashout
          </p>
          <div className="space-y-2.5 text-sm">
            <Row label="Method" value={method?.title || '-'} />
            <Row label="Wallet deduction" value={moneyLabel(cashoutGrossAmount)} valueBold />
            {hasTip && <Row label={`Tip to ${tipAgent?.name || 'agent'}`} value={moneyLabel(cashoutTipAmount)} valueBold />}
            <Row label="Withdrawal amount" value={moneyLabel(cashoutNetAmount)} valueBold />
            {payoutName && <Row label={labels.nameLabel} value={payoutName} />}
            {walletAddress && <Row label={labels.tagLabel} value={walletAddress} mono />}
            {payoutDetails && <Row label="Details" value={payoutDetails} />}
            <Row label="Current Balance" value={moneyLabel(balanceUsd)} />
          </div>
        </div>

        <div className="rounded-2xl p-4 text-sm" style={{ background: '#FEF3C7', border: '1px solid rgba(180,83,9,0.25)', color: '#92400E' }}>
          Once submitted, your cashout request will be processed by our team. You can track it in your transaction history.
        </div>
      </div>
    );
  };

  if (step === 0) {
    return (
      <>
        <div className="flex-1 overflow-y-auto">
          <div className="mx-auto flex min-h-full max-w-2xl flex-col gap-5 px-4 pb-7 pt-4 sm:px-8">
            <section
              className="overflow-hidden rounded-[28px] border p-5"
              style={{
                background: 'linear-gradient(135deg, rgba(1,18,30,0.94), rgba(10,15,36,0.92) 54%, rgba(25,10,42,0.92))',
                borderColor: 'rgba(255,255,255,0.18)',
                boxShadow: '0 18px 44px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.08)'
              }}
              data-testid="wallet-dashboard-balance-card"
            >
              <p className="text-[11px] font-black uppercase tracking-[0.22em]" style={{ color: '#C7F7FF' }}>
                Wallet Balance
              </p>
              <div className="mt-3 flex items-center gap-3">
                <span
                  className="grid h-12 w-12 place-items-center rounded-2xl"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)' }}
                  aria-hidden
                >
                  <Wallet className="h-7 w-7 text-white" />
                </span>
                <span
                  className="mono min-w-0 flex-1 text-5xl font-black leading-none"
                  style={{
                    color: '#22FF88',
                    textShadow: '0 0 18px rgba(34,255,136,0.24)',
                  }}
                >
                  {balanceVisible ? `$${balanceUsd.toFixed(2)}` : '$••••'}
                </span>
                <button
                  type="button"
                  onClick={() => setBalanceVisible((visible) => !visible)}
                  className="grid h-11 w-11 shrink-0 place-items-center rounded-full transition active:scale-95 hover:brightness-110"
                  style={{
                    background: 'rgba(255,255,255,0.08)',
                    border: '1px solid rgba(255,230,109,0.52)',
                    color: '#FFE66D',
                    boxShadow: '0 8px 18px rgba(0,0,0,0.22)',
                  }}
                  aria-label={balanceVisible ? 'Hide wallet balance' : 'Show wallet balance'}
                  aria-pressed={!balanceVisible}
                  data-testid="wallet-balance-visibility-toggle"
                >
                  {balanceVisible ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>

              <div
                className="mt-5 flex items-center gap-3 rounded-2xl px-4 py-3"
                style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)' }}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-black uppercase tracking-[0.24em]" style={{ color: '#A7EAF2' }}>Free Play</p>
                  <p
                    className="display mt-1 text-3xl font-black"
                    style={{
                      color: '#FFE66D',
                      textShadow: '0 0 16px rgba(255,230,109,0.22)',
                    }}
                  >
                    {balanceVisible ? freePlayBalance : '••'}<span className="ml-1 text-xl">FP</span>
                  </p>
                </div>
                {marketplaceEnabled && (
                  <button
                    type="button"
                    onClick={() => navigate(marketplacePath)}
                    className="grid h-10 w-10 shrink-0 place-items-center rounded-full transition active:scale-95 hover:brightness-110"
                    style={{
                      background: 'rgba(255,230,109,0.16)',
                      border: '1px solid rgba(255,230,109,0.56)',
                      color: '#FFE66D',
                      boxShadow: '0 8px 18px rgba(0,0,0,0.22)',
                    }}
                    aria-label="Open Marketplace"
                    data-testid="wallet-freeplay-marketplace-link"
                  >
                    <ChevronRight className="h-5 w-5" strokeWidth={3} />
                  </button>
                )}
              </div>
            </section>

            <div className="space-y-3" data-testid="wallet-dashboard-actions">
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => openWalletAction('deposit')}
                  className="h-14 rounded-2xl display text-base font-black transition active:scale-[0.98]"
                  style={{
                    background: 'linear-gradient(180deg, #27E582, #10A85A)',
                    color: '#03140D',
                    boxShadow: '0 12px 26px rgba(34,197,94,0.24)'
                  }}
                >
                  Deposit
                </button>
                <button
                  type="button"
                  onClick={() => openWalletAction('withdrawal')}
                  className="h-14 rounded-2xl display text-base font-black text-white transition active:scale-[0.98]"
                  style={{
                    background: 'linear-gradient(180deg, #FFB347, #F97316)',
                    boxShadow: '0 12px 26px rgba(249,115,22,0.24)'
                  }}
                >
                  Cashout
                </button>
              </div>
              <button
                type="button"
                onClick={() => setTransferPanelOpen((open) => !open)}
                className="flex min-h-[64px] w-full items-center justify-start gap-3 rounded-2xl px-5 py-3 text-white transition active:scale-[0.98] sm:min-h-[68px] sm:px-6"
                style={{
                  background: 'linear-gradient(180deg, #5B7CFF, #6D28D9)',
                  boxShadow: '0 12px 26px rgba(99,102,241,0.24)'
                }}
                data-testid="wallet-dashboard-internal-transfer"
                aria-expanded={transferPanelOpen}
              >
                <ArrowLeftRight className="h-7 w-7 shrink-0" strokeWidth={2.7} />
                <span className="min-w-0 text-left">
                  <span className="display block text-lg font-black leading-tight sm:text-xl">Transfer</span>
                  <span className="mt-0.5 block truncate text-xs font-extrabold leading-tight text-white/78 sm:text-sm">
                    Send money to friends
                  </span>
                </span>
              </button>
              {transferPanelOpen && (
                <WalletTransferPanel
                  walletUsd={balanceUsd}
                  onWalletChanged={async () => {
                    await refresh?.();
                    await checkAuth?.();
                  }}
                />
              )}
            </div>

            <section
              className="rounded-[24px] border p-4"
              style={{
                background: 'rgba(1,18,30,0.56)',
                borderColor: 'rgba(255,255,255,0.14)',
                boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.05)'
              }}
              data-testid="wallet-dashboard-transactions"
            >
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <p className="display text-lg font-black text-white">Recent Transactions</p>
                  <p className="text-xs font-bold" style={{ color: 'rgba(255,255,255,0.56)' }}>Latest wallet activity</p>
                </div>
                <RefreshCw className="h-4 w-4" style={{ color: '#A7EAF2' }} />
              </div>

              {recentTransactions.length === 0 ? (
                <div className="rounded-2xl px-4 py-8 text-center" style={{ background: 'rgba(0,0,0,0.18)', color: 'rgba(255,255,255,0.72)' }}>
                  <p className="display font-black text-white">No transactions yet</p>
                  <p className="mt-1 text-sm">Deposits and cashouts will show here.</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {recentTransactions.map((txn, index) => {
                    const title = transactionLabel(txn);
                    const amountLabel = transactionAmount(txn);
                    const status = transactionStatus(txn);
                    const dateLabel = transactionDate(txn);
                    const isCashout = title.toLowerCase().includes('cashout');
                    return (
                      <div
                        key={txn?.id || txn?._id || `${title}-${index}`}
                        className="flex items-center gap-3 rounded-2xl px-3 py-3"
                        style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.10)' }}
                      >
                        <span
                          className="grid h-10 w-10 shrink-0 place-items-center rounded-xl"
                          style={{ background: isCashout ? 'rgba(249,115,22,0.18)' : 'rgba(34,197,94,0.18)', color: isCashout ? '#FDBA74' : '#86EFAC' }}
                          aria-hidden
                        >
                          <Wallet className="h-5 w-5" />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="display block truncate text-sm font-black text-white">{title}</span>
                          <span className="block truncate text-[11px] font-bold capitalize" style={{ color: 'rgba(255,255,255,0.52)' }}>
                            {status}{dateLabel ? ` - ${dateLabel}` : ''}
                          </span>
                        </span>
                        {amountLabel && (
                          <span className="mono text-sm font-black" style={{ color: amountLabel.startsWith('-') ? '#FDBA74' : '#86EFAC' }}>
                            {amountLabel}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
          </div>
        </div>
      </>
    );
  }

  if (checkoutPanel) {
    const checkoutAsLink = Boolean(checkoutPanel.linkReady);
    return (
      <div
        className={checkoutAsLink
          ? 'fixed inset-0 z-[70] flex items-center justify-center px-4'
          : 'fixed inset-0 z-[70] flex flex-col'}
        style={{
          background: checkoutAsLink ? 'rgba(11,15,12,0.62)' : '#FFFFFF',
          color: '#0B0F0C',
          fontFamily: "'Plus Jakarta Sans', sans-serif"
        }}
        data-testid={checkoutAsLink ? 'payment-link-ready-modal' : 'embedded-checkout-panel'}
      >
        <div className={checkoutAsLink
          ? 'w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden'
          : 'w-full h-full flex flex-col bg-white overflow-hidden'}>
        <div className={`${checkoutAsLink ? 'px-5 py-4' : 'shrink-0 px-4 sm:px-6 py-3'} flex items-center gap-3 border-b`} style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
          <button
            onClick={() => setCheckoutPanel(null)}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: '#F3F4F6', color: '#0B0F0C' }}
            aria-label="Back to amount selection"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-wider font-bold" style={{ color: 'var(--vl-brand-primary, #D4AF37)' }}>
              {checkoutAsLink ? 'Website Deposit' : 'Secure Checkout'}
            </p>
            <p className="display font-extrabold truncate" style={{ color: '#0B0F0C' }}>
              {checkoutPanel.methodTitle} · ${Number(checkoutPanel.amount || 0).toFixed(2)}
            </p>
          </div>
          <button
            onClick={checkoutAsLink ? () => setCheckoutPanel(null) : close}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: '#F3F4F6', color: '#0B0F0C' }}
            aria-label={checkoutAsLink ? 'Close payment link modal' : 'Close checkout'}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div
          className={`${checkoutAsLink ? 'px-5' : 'px-4'} py-2 text-xs text-center`}
          style={{
            background: checkoutPanel.failed ? '#FEF2F2' : checkoutPanel.verified ? '#F0FDF4' : 'rgba(255,217,61,0.16)',
            color: checkoutPanel.failed ? '#991B1B' : checkoutPanel.verified ? '#166534' : '#6B4800'
          }}
        >
          {checkoutPanel.verified
            ? 'Payment verified. Your wallet has been updated.'
            : checkoutPanel.failed
              ? 'Payment was not completed. You can go back and try again.'
              : checkoutAsLink
                ? 'Open the link to complete payment. Your wallet updates automatically when payment is verified.'
                : 'Keep this panel open after payment. Your wallet updates automatically when payment is verified.'}
        </div>
        {checkoutPanel.loading ? (
          <div className={checkoutAsLink ? 'px-6 py-10' : 'flex-1 flex items-center justify-center px-6 py-10'}>
            <div className="w-full max-w-sm text-center">
              <style>{`
                @keyframes checkout-gold-spin {
                  to { transform: rotate(360deg); }
                }
                @keyframes checkout-gold-pulse {
                  0%, 100% { opacity: 0.28; transform: scale(0.82); }
                  50% { opacity: 0.75; transform: scale(1.18); }
                }
                @keyframes checkout-gold-sweep {
                  0% { transform: rotate(0deg) translateX(34px) rotate(0deg); opacity: 0.15; }
                  50% { opacity: 1; }
                  100% { transform: rotate(360deg) translateX(34px) rotate(-360deg); opacity: 0.15; }
                }
              `}</style>
              <div className="relative w-28 h-28 mx-auto flex items-center justify-center" aria-label="Creating checkout">
                <span
                  className="absolute inset-2 rounded-full"
                  style={{
                    background: 'radial-gradient(circle, rgba(255,217,61,0.42) 0%, rgba(255,217,61,0.14) 48%, rgba(255,217,61,0) 72%)',
                    boxShadow: '0 0 42px rgba(255,217,61,0.55)',
                    animation: 'checkout-gold-pulse 1.7s ease-in-out infinite',
                  }}
                  aria-hidden
                />
                <span
                  className="absolute w-24 h-24 rounded-full"
                  style={{
                    border: '2px solid rgba(212,175,55,0.24)',
                    borderTopColor: 'var(--vl-brand-primary, #D4AF37)',
                    borderRightColor: '#FFD93D',
                    filter: 'drop-shadow(0 0 12px rgba(255,217,61,0.7))',
                    animation: 'checkout-gold-spin 1.05s linear infinite',
                  }}
                  aria-hidden
                />
                <span
                  className="absolute w-4 h-4 rounded-full"
                  style={{
                    background: '#FFD93D',
                    boxShadow: '0 0 18px rgba(255,217,61,0.95), 0 0 34px rgba(212,175,55,0.75)',
                    animation: 'checkout-gold-sweep 1.05s linear infinite',
                  }}
                  aria-hidden
                />
                <div
                  className="relative w-16 h-16 rounded-full flex items-center justify-center overflow-hidden"
                  style={{
                    background: brandLogoUrl ? '#FFFFFF' : 'linear-gradient(180deg, #FFF3A3 0%, #FFD93D 45%, var(--vl-brand-primary, #D4AF37) 100%)',
                    color: brandLogoUrl ? '#0B0F0C' : '#2B1E00',
                    border: '2px solid rgba(255,255,255,0.72)',
                    boxShadow: 'inset 0 2px 8px rgba(255,255,255,0.55), 0 14px 34px rgba(212,175,55,0.42), 0 0 0 5px rgba(255,217,61,0.18)',
                  }}
                >
                  {brandLogoUrl ? (
                    <SafeImage
                      src={brandLogoUrl}
                      alt={branding?.brand_name || 'Brand logo'}
                      className="w-full h-full object-cover"
                      data-testid="checkout-loader-brand-logo"
                      loading="eager"
                      fallback={<RefreshCw className="w-8 h-8 animate-spin" />}
                    />
                  ) : (
                    <RefreshCw
                      className="w-8 h-8"
                      strokeWidth={3}
                      style={{ animation: 'checkout-gold-spin 1.05s linear infinite' }}
                    />
                  )}
                  <span
                    className="absolute inset-0 rounded-full pointer-events-none"
                    style={{
                      background: 'radial-gradient(circle at 35% 25%, rgba(255,255,255,0.42), transparent 36%)',
                      boxShadow: 'inset 0 0 16px rgba(43,30,0,0.18)',
                    }}
                    aria-hidden
                  />
                </div>
              </div>
              <p className="display text-2xl font-extrabold mt-5" style={{ color: '#0B0F0C' }}>
                {checkoutAsLink ? 'Creating payment link' : 'Creating secure checkout'}
              </p>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: '#64748B' }}>
                Hold on. We are generating your {checkoutPanel.methodTitle} {checkoutAsLink ? 'payment link' : 'payment screen'} now.
              </p>
            </div>
          </div>
        ) : checkoutPanel.verified || checkoutPanel.failed ? (
          <div className={checkoutAsLink ? 'px-6 py-10' : 'flex-1 flex items-center justify-center px-6 py-10'}>
            <div className="w-full max-w-sm text-center">
              <div
                className="w-20 h-20 mx-auto rounded-full flex items-center justify-center"
                style={{
                  background: checkoutPanel.verified ? '#DCFCE7' : '#FEE2E2',
                  color: checkoutPanel.verified ? '#075F75' : '#DC2626'
                }}
              >
                {checkoutPanel.verified ? (
                  <CheckCircle2 className="w-11 h-11" strokeWidth={3} />
                ) : (
                  <XCircle className="w-11 h-11" strokeWidth={3} />
                )}
              </div>
              <p className="display text-2xl font-extrabold mt-5" style={{ color: '#0B0F0C' }}>
                {checkoutPanel.verified ? 'Payment verified' : 'Payment not completed'}
              </p>
              <p className="text-sm mt-2 leading-relaxed" style={{ color: '#64748B' }}>
                {checkoutPanel.verified
                  ? `Your $${Number(checkoutPanel.amount || 0).toFixed(2)} ${checkoutPanel.methodTitle} deposit has been credited.`
                  : (checkoutPanel.errorMessage || 'No wallet credit was added for this attempt. Go back and choose an amount to try again.')}
              </p>
              {checkoutPanel.failed && (
                <button
                  onClick={() => setCheckoutPanel(null)}
                  className="mt-6 w-full h-12 rounded-xl display font-extrabold"
                  style={{ background: '#F3F4F6', color: '#0B0F0C' }}
                >
                  Try Again
                </button>
              )}
            </div>
          </div>
        ) : checkoutAsLink ? (
          <div className="px-6 py-7">
            <div className="text-center">
              <div
                className="w-16 h-16 mx-auto rounded-full flex items-center justify-center"
                style={{ background: '#DCFCE7', color: '#075F75' }}
              >
                <CheckCircle2 className="w-9 h-9" strokeWidth={3} />
              </div>
              <h2 className="display text-2xl font-extrabold mt-5" style={{ color: '#0B0F0C' }}>
                Your link is ready
              </h2>
            </div>
            <div className="mt-6">
              <button
                type="button"
                onClick={() => openPaymentLinkExternally(checkoutPanel.originalUrl || checkoutPanel.url)}
                className="w-full h-12 rounded-xl display font-extrabold"
                style={{ background: 'linear-gradient(180deg,#22C55E,#15803D)', color: '#FFFFFF', boxShadow: '0 8px 22px rgba(34,197,94,0.35)' }}
                data-testid="payment-link-proceed"
              >
                Proceed to Pay
              </button>
            </div>
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col">
            <iframe
              title="Secure payment checkout"
              src={checkoutPanel.url}
              className="flex-1 w-full border-0"
              allow={checkoutPanel.applePay ? 'payment; publickey-credentials-get; clipboard-read; clipboard-write' : 'payment; clipboard-read; clipboard-write'}
              allowPaymentRequest
              {...(checkoutPanel.applePay ? {} : {
                sandbox: 'allow-forms allow-same-origin allow-scripts allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation allow-storage-access-by-user-activation'
              })}
            />
          </div>
        )}
        <div className={`${checkoutAsLink ? 'px-5 py-4' : 'shrink-0 px-4 sm:px-6 py-3'} border-t`} style={{ borderColor: 'rgba(0,0,0,0.08)', background: '#FFFFFF' }}>
          <button
            onClick={close}
            className="w-full h-12 rounded-xl display font-extrabold flex items-center justify-center gap-2"
            style={{ background: '#0B0F0C', color: '#FFFFFF' }}
          >
            <ChevronLeft className="w-4 h-4" /> Return to Home
          </button>
        </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <button
        onClick={requestClose}
        className="fixed top-4 right-4 z-[70] w-11 h-11 rounded-full flex items-center justify-center transition active:scale-95 hover:scale-105"
        style={{
          background: 'rgba(0,0,0,0.55)',
          border: '2px solid rgba(255,255,255,0.9)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          boxShadow: '0 6px 18px rgba(0,0,0,0.35)'
        }}
        data-testid="wallet-close"
        aria-label="Close"
      >
        <X className="w-5 h-5 text-white" strokeWidth={3} />
      </button>

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-4 sm:px-8 py-10 sm:py-12">
          <Stepper step={step} accent={accent} accentDark={accentDark} labels={stepperLabels} />

          <div className="flex justify-start mt-8 mb-5">
            <div
              className="flex items-center gap-2 px-4 py-2 rounded-xl"
              style={{
                background: mode === 'deposit' ? '#F0FDF4' : '#FFF7ED',
                border: `1px solid ${accent}55`
              }}
              data-testid="wallet-current-balance"
            >
              <Wallet className="w-4 h-4" style={{ color: accentDark }} />
              <span className="text-xs uppercase tracking-wider font-bold" style={{ color: accentDark }}>Current Balance:</span>
              <span className="display font-extrabold mono" style={{ color: accentDark }}>${balanceUsd.toFixed(2)}</span>
            </div>
          </div>

          {mode === 'withdrawal' ? (
            step === 1
              ? renderWithdrawalAmountStep()
              : step === 2
                ? renderWithdrawalMethodStep()
                : step === 3
                  ? renderWithdrawalDetailsStep()
                  : renderWithdrawalReviewStep()
          ) : (
            step === 1 ? renderMethodList() : renderDepositAmountStep()
          )}
        </div>
      </div>

      <div
        className="shrink-0 border-t"
        style={{
          background: '#FFFFFF',
          borderColor: 'rgba(0,0,0,0.08)',
          boxShadow: '0 -8px 24px rgba(0,0,0,0.05)'
        }}
        data-testid="wallet-footer"
      >
        <div className="max-w-2xl mx-auto px-4 sm:px-8 py-4 space-y-2">
          {step === 1 && (
            <Button
              onClick={() => {
                if (mode === 'withdrawal') {
                  if (validateWithdrawalAmountStep()) setStep(2);
                  return;
                }
                if (method) setStep(2);
                else toast.error('Select a payment method');
              }}
              disabled={mode === 'withdrawal' ? (!cashoutGrossAmount || exceedsDailyWithdrawalCap) : !method}
              className="w-full h-14 display font-bold text-base rounded-xl disabled:opacity-50"
              style={{
                background: `linear-gradient(180deg, ${accent}, ${accentDark})`,
                color: '#fff',
                boxShadow: (mode === 'withdrawal' ? (cashoutGrossAmount && !exceedsDailyWithdrawalCap) : method) ? `0 8px 22px ${accent}66` : 'none'
              }}
              data-testid="wallet-next-btn"
            >
              Next →
            </Button>
          )}

          {step === 2 && mode === 'deposit' && (
            <>
              <Button
                onClick={() => {
                  if (isSupportContactMethod(activeMethod || method)) {
                    openSupportForLoad();
                    return;
                  }
                  submit();
                }}
                disabled={processing || (!isSupportContactMethod(activeMethod || method) && (!amount || (!isApiGatewayMethod(activeMethod || method) && !proofFile)))}
                className="w-full h-14 display font-bold text-base rounded-xl"
                style={{
                  background: 'linear-gradient(180deg,#22C55E,#15803D)',
                  color: '#fff',
                  boxShadow: '0 8px 22px rgba(34,197,94,0.4)'
                }}
                data-testid="fs-wallet-submit"
              >
                {processing ? (
                  <div className="spinner w-5 h-5" />
                ) : (
                  isSupportContactMethod(activeMethod || method)
                    ? 'Open Support →'
                    : isApiGatewayMethod(activeMethod || method)
                    ? 'Create Payment Link →'
                    : 'Confirm Purchase'
                )}
              </Button>
              <button
                onClick={close}
                className="w-full text-sm display font-bold flex items-center justify-center gap-1"
                style={{ color: '#6B7280' }}
                data-testid="wallet-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back to Home
              </button>
            </>
          )}

          {step === 2 && mode === 'withdrawal' && (
            <>
              <Button
                onClick={() => {
                  if (!method) { toast.error('Select a cashout method'); return; }
                  setStep(3);
                }}
                disabled={!method}
                className="w-full h-14 display font-bold text-base rounded-xl disabled:opacity-50"
                style={{
                  background: `linear-gradient(180deg, ${accent}, ${accentDark})`,
                  color: '#fff',
                  boxShadow: method ? `0 8px 22px ${accent}66` : 'none'
                }}
                data-testid="wallet-next-step3-btn"
              >
                Next →
              </Button>
              <button
                onClick={() => setStep(1)}
                className="w-full text-sm display font-bold flex items-center justify-center gap-1"
                style={{ color: '#6B7280' }}
                data-testid="wallet-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back to amount
              </button>
            </>
          )}

          {step === 3 && mode === 'withdrawal' && (
            <>
              <Button
                onClick={() => {
                  const labels = cashoutMethodLabels(activeMethod || method);
                  if (!String(payoutName || '').trim() || !String(walletAddress || '').trim()) {
                    toast.error(`Enter ${labels.nameLabel} and ${labels.tagLabel}`);
                    return;
                  }
                  setStep(4);
                }}
                disabled={!String(payoutName || '').trim() || !String(walletAddress || '').trim()}
                className="w-full h-14 display font-bold text-base rounded-xl disabled:opacity-50"
                style={{
                  background: `linear-gradient(180deg, ${accent}, ${accentDark})`,
                  color: '#fff',
                  boxShadow: (String(payoutName || '').trim() && String(walletAddress || '').trim()) ? `0 8px 22px ${accent}66` : 'none'
                }}
                data-testid="wallet-next-step4-btn"
              >
                Next →
              </Button>
              <button
                onClick={() => setStep(2)}
                className="w-full text-sm display font-bold flex items-center justify-center gap-1"
                style={{ color: '#6B7280' }}
                data-testid="wallet-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back to cashout method
              </button>
            </>
          )}

          {step === 4 && mode === 'withdrawal' && (
            <>
              <Button
                onClick={() => submit()}
                disabled={processing}
                className="w-full h-14 display font-bold text-base rounded-xl"
                style={{
                  background: 'linear-gradient(180deg,#F97316,#C2410C)',
                  color: '#fff',
                  boxShadow: '0 8px 22px rgba(249,115,22,0.4)'
                }}
                data-testid="fs-wallet-submit"
              >
                {processing ? <div className="spinner w-5 h-5" /> : 'Confirm Cashout'}
              </Button>
              <button
                onClick={() => setStep(3)}
                className="w-full text-sm display font-bold flex items-center justify-center gap-1"
                style={{ color: '#6B7280' }}
                data-testid="wallet-back-btn"
              >
                <ChevronLeft className="w-4 h-4" /> Back to transaction details
              </button>
            </>
          )}
        </div>
      </div>

      <Dialog open={confirmCloseOpen} onOpenChange={setConfirmCloseOpen}>
        <DialogContent
          overlayClassName="!bg-black/65 backdrop-blur-md !z-[100]"
          className="max-w-md border-0 p-7 sm:p-10 rounded-3xl !z-[101]"
          style={{
            background: '#FFFFFF',
            color: '#0B0F0C',
            boxShadow: '0 30px 70px rgba(0,0,0,0.45)'
          }}
        >
          <DialogHeader>
            <DialogTitle
              className="display font-extrabold text-center"
              style={{
                color: '#0B0F0C',
                fontSize: '28px',
                lineHeight: 1.15,
                letterSpacing: '-0.01em'
              }}
            >
              Are you sure you want to leave?
            </DialogTitle>
          </DialogHeader>
          <p
            className="text-center mt-4"
            style={{ color: '#1F2937', fontSize: '17px', fontWeight: 500 }}
          >
            Your transaction will be canceled if you proceed.
          </p>
          <div className="grid grid-cols-2 gap-4 mt-7">
            <button
              onClick={() => { setConfirmCloseOpen(false); close(); }}
              className="display font-extrabold rounded-2xl text-white text-sm sm:text-base px-3 py-4 transition active:scale-[0.98] hover:brightness-110"
              style={{
                background: '#E11D2E',
                boxShadow: '0 10px 24px rgba(225,29,46,0.45), inset 0 1px 0 rgba(255,255,255,0.18)'
              }}
              data-testid="confirm-leave-yes-btn"
            >
              Yes, I want to go out
            </button>
            <button
              onClick={() => setConfirmCloseOpen(false)}
              className="display font-extrabold rounded-2xl text-white text-sm sm:text-base px-3 py-4 transition active:scale-[0.98] hover:brightness-110"
              style={{
                background: '#15A036',
                boxShadow: '0 10px 24px rgba(21,160,54,0.45), inset 0 1px 0 rgba(255,255,255,0.18)'
              }}
              data-testid="confirm-leave-continue-btn"
            >
              I want to continue
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
